/**
 * 任務瀏覽頁面
 * 顯示當前駕駛資訊、車輛資訊和任務列表
 */

import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { useLiff } from '@/contexts/LiffContext'
import { getDriver } from '@/services/driverService'
import {
  getDriverTasks,
  updateTaskStatus,
  rejectTask,
  getShipsideCards,
  grabShipsideTask,
  reserveShipside,
  getMyReservations,
  bindReservation,
  cancelReservation,
  cancelMyTask,
  batchCompleteTask,
  updateContainerNumber,
  validateContainerNo,
} from '@/services/taskService'
import { getCurrentDuty, validateHandover } from '@/services/vehicleService'
import type { Driver, Vehicle, VehicleState, Task, TaskStatus, ShipsideCard, ShipsideCardRow, GrabReservation, BlockingTask, HandoverValidationSummary } from '@/types'
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  Chip,
  Grid,
  IconButton,
  TextField,
  Tabs,
  Tab,
} from '@mui/material'
import AssignmentIcon from '@mui/icons-material/Assignment'
import ExitToAppIcon from '@mui/icons-material/ExitToApp'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import CloseIcon from '@mui/icons-material/Close'
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import TouchAppIcon from '@mui/icons-material/TouchApp'
import DescriptionIcon from '@mui/icons-material/Description'
import EditNoteIcon from '@mui/icons-material/EditNote'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import RefreshIcon from '@mui/icons-material/Refresh'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import BarcodeScanner from '@/components/BarcodeScanner'

const TaskBrowsePage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { lineUser } = useLiff()

  // 從 location.state 取得資料
  const stateDriver = location.state?.driver as Driver | undefined
  const stateVehicle = location.state?.vehicle as Vehicle | undefined
  const stateVehicleState = location.state?.vehicleState as VehicleState | undefined

  // ========== State ==========
  const [driver, setDriver] = useState<Driver | null>(stateDriver || null)
  const [vehicle, setVehicle] = useState<Vehicle | null>(stateVehicle || null)
  const [vehicleState, setVehicleState] = useState<VehicleState | null>(
    stateVehicleState || null
  )
  const [loading, setLoading] = useState(true)
  const [tasks, setTasks] = useState<Task[]>([])
  const [selectedTask, setSelectedTask] = useState<Task | null>(null)
  const [taskDetailDialogOpen, setTaskDetailDialogOpen] = useState(false)
  const [updatingTaskStatus, setUpdatingTaskStatus] = useState(false)
  const [messageDialogOpen, setMessageDialogOpen] = useState(false)
  const [messageText, setMessageText] = useState('')
  const [messageSeverity, setMessageSeverity] = useState<'success' | 'error' | 'warning'>('success')
  const [countdown, setCountdown] = useState(3)
  // 訊息對話框關閉時要執行的後續動作（如接單成功後切頁籤）；OK 與倒數結束共用同一動作
  const messageActionRef = useRef<(() => void) | null>(null)
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [pendingStatus, setPendingStatus] = useState<Task['status'] | null>(null)
  const [confirmAction, setConfirmAction] = useState<'update' | 'reject'>('update')
  const [containerNumberInput, setContainerNumberInput] = useState('')
  const [isEditingContainerNumber, setIsEditingContainerNumber] = useState(false)
  const [isDualMode, setIsDualMode] = useState(false) // 雙櫃模式：從合併卡片進入詳情
  const [checkDigitWarningOpen, setCheckDigitWarningOpen] = useState(false)
  const checkDigitResolveRef = useRef<((confirmed: boolean) => void) | null>(null)
  const [scanDialogOpen, setScanDialogOpen] = useState(false)
  const [scannerDialogOpen, setScannerDialogOpen] = useState(false)
  const [handoverWarningDialogOpen, setHandoverWarningDialogOpen] = useState(false)
  const [blockingTasks, setBlockingTasks] = useState<BlockingTask[]>([])
  const [blockingSummary, setBlockingSummary] = useState<HandoverValidationSummary | null>(null)
  const [validatingHandover, setValidatingHandover] = useState(false)

  // Tab 相關狀態 - 從 URL 參數讀取初始值 (?tab=1 為船邊櫃任務)
  const initialTab = parseInt(searchParams.get('tab') || '0', 10)
  const [currentTab, setCurrentTab] = useState<number>(initialTab) // 0: 我的任務, 1: 船邊櫃任務
  const [shipsideCards, setShipsideCards] = useState<ShipsideCard[]>([])
  // 頁籤徽章顯示「可搶總數」而非卡片數：兩層聚合後一張卡可含多筆任務，卡片數不再等於可搶量
  const [shipsideTotalRemaining, setShipsideTotalRemaining] = useState(0)
  // 明細行超過 3 行時收合；key 為 cardKey
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set())
  // 搶單中的明細行（cardKey + 規格組合），避免整頁按鈕一起 disable
  const [grabbingRowKey, setGrabbingRowKey] = useState<string | null>(null)
  // 雙拖詢問：20 呎且該明細行剩餘 >= 2 時，點接單先問要接幾櫃
  // （外車照抄此流程：src/ext/ExtBatchPage.tsx 的 doubleTowAsk／handleGrab，2026-09-29）
  const [doubleTowAsk, setDoubleTowAsk] = useState<{ card: ShipsideCard; row: ShipsideCardRow } | null>(null)
  // ===== 名額卡（openspec add-import-grab-quota）=====
  // 進口卡片搶單只登記名額（不綁 mission）；到場在「我的任務」選種類才綁。
  const [reservations, setReservations] = useState<GrabReservation[]>([])
  const [reservingKey, setReservingKey] = useState<string | null>(null)
  const [bindingId, setBindingId] = useState<string | null>(null)
  // 選種類前 double check（客戶 2026-09-10）：綁定後不能改，先確認一次
  const [bindConfirm, setBindConfirm] = useState<{ reservation: GrabReservation; kind: '40' | '20' | '20x2' } | null>(null)
  const KIND_LABEL: Record<string, string> = { '40': '40 呎', '20': '20 呎單拖', '20x2': '20 呎雙拖' }
  const isImportCard = (card: ShipsideCard) => card.importExportType === '進口'
  // 名額卡與執行中任務用同一把尺（客戶 2026-09-10）：有執行中任務不能領名額卡；手上有名額卡不能再搶任何單
  const holdsReservation = reservations.length > 0
  const hasInProgressTask = tasks.some((t) => t.status === 'IN_PROGRESS')
  const grabBlockedLabel = holdsReservation ? '已在排隊中' : hasInProgressTask ? '有執行中任務' : null
  /** 進口 + 搶單來的任務不顯示起訖點：司機排隊拿到哪櫃是當下決定 */
  const hideRoute = (t: { importExportType?: string; grabbed?: boolean }) =>
    t.importExportType === '進口' && t.grabbed === true
  const [inProgressExpanded, setInProgressExpanded] = useState(false) // 執行中任務是否展開
  // 取消執行中船邊櫃出口任務（IN_PROGRESS + 出口 + 司機本人）
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  const [cancelTarget, setCancelTarget] = useState<Task | null>(null)
  // 推斷配對的夥伴（搶單雙拖無 paired_mission_id，需由呼叫端顯式帶入）
  const [cancelPartner, setCancelPartner] = useState<Task | null>(null)
  const [cancellingTask, setCancellingTask] = useState(false)
  // 放櫃備註詳情 dialog
  const [remarkDetailOpen, setRemarkDetailOpen] = useState(false)
  const [remarkDetailContent, setRemarkDetailContent] = useState('')

  // 完成任務模式 - 從 URL 參數讀取 (?action=complete)
  const isCompleteMode = searchParams.get('action') === 'complete'
  const [completeModeProcessed, setCompleteModeProcessed] = useState(false) // 防止重複處理
  const [notOnDutyDialogOpen, setNotOnDutyDialogOpen] = useState(false) // 未上工提示
  const [notOnDutyCountdown, setNotOnDutyCountdown] = useState(3) // 未上工倒數
  const [refreshing, setRefreshing] = useState(false) // 刷新中
  const [nextRefreshIn, setNextRefreshIn] = useState(120) // 自動刷新倒數（秒）

  // ========== Refs ==========

  // 關閉訊息對話框並執行後續動作（OK 按鈕與倒數結束共用；點 OK = 提前完成 3 秒後要做的事）
  const closeMessageDialog = useCallback(() => {
    const action = messageActionRef.current
    messageActionRef.current = null
    setMessageDialogOpen(false)
    setCountdown(3) // 重置倒數
    action?.()
  }, [])

  // ========== 倒數計時器（訊息對話框） ==========
  useEffect(() => {
    let timer: NodeJS.Timeout
    if (messageDialogOpen && countdown > 0) {
      timer = setTimeout(() => {
        setCountdown(countdown - 1)
      }, 1000)
    } else if (messageDialogOpen && countdown === 0) {
      closeMessageDialog()
    }
    return () => clearTimeout(timer)
  }, [messageDialogOpen, countdown, closeMessageDialog])

  // ========== 倒數計時器（未上工提示） ==========
  useEffect(() => {
    if (notOnDutyDialogOpen && notOnDutyCountdown > 0) {
      const timer = setTimeout(() => {
        setNotOnDutyCountdown(notOnDutyCountdown - 1)
      }, 1000)
      return () => clearTimeout(timer)
    } else if (notOnDutyDialogOpen && notOnDutyCountdown === 0) {
      // 倒數結束，跳轉到首頁
      navigate('/home', { replace: true })
    }
  }, [notOnDutyDialogOpen, notOnDutyCountdown, navigate])

  // ========== 載入資料 ==========
  useEffect(() => {
    const loadData = async () => {
      if (!lineUser) return

      try {
        // 確認司機資料
        const driverData = await getDriver(lineUser.userId)
        if (!driverData) {
          navigate('/binding', { replace: true })
          return
        }

        // 如果有 state 資料，優先使用
        if (stateDriver && stateVehicle && stateVehicleState) {
          setDriver(stateDriver)
          setVehicle(stateVehicle)
          setVehicleState(stateVehicleState)
        } else {
          // 沒有 state 資料時，設定司機資料
          setDriver(driverData)

          // 如果司機有未交班記錄，從 API 載入當前出勤資訊
          if (driverData.hasUnfinishedDuty) {
            console.log('[TaskBrowsePage] 司機有未交班記錄，載入當前出勤資訊...')
            const currentDutyData = await getCurrentDuty()
            if (currentDutyData) {
              console.log('[TaskBrowsePage] 載入到車輛資訊:', currentDutyData.vehicleNo)
              // 設定車輛資訊
              setVehicle({
                vehicleId: currentDutyData.vehicleId,
                vehicleNo: currentDutyData.vehicleNo,
                vehicleType: currentDutyData.vehicleType,
                vehicleTypeName: currentDutyData.vehicleTypeName,
                status: 'IN_USE',
                statusName: '使用中',
                currentDriverId: driverData.driverId,
                currentDriverName: driverData.driverName,
                fuelMileageRequired: currentDutyData.fuelMileageRequired,
              })
              // 設定接班時的車輛狀態（用於交班時計算差異）
              setVehicleState({
                vehicleId: currentDutyData.vehicleId,
                vehicleNo: currentDutyData.vehicleNo,
                isEnabled: true,
                usageState: 'IN_USE',
                currentDriver: {
                  driverId: driverData.driverId,
                  driverName: driverData.driverName,
                },
                lastReportTime: currentDutyData.clockInTime,
                // 接班時的油量和里程（用於交班時計算差異）
                fuelLevel: currentDutyData.onDutyFuel,
                mileage: currentDutyData.onDutyMileage,
              } as VehicleState & { fuelLevel: number; mileage: number })
            } else {
              console.log('[TaskBrowsePage] 無法載入當前出勤資訊')
              setVehicle(null)
              setVehicleState(null)
            }
          } else {
            // 司機未上工，顯示提示並倒數後回首頁
            setNotOnDutyDialogOpen(true)
            setLoading(false)
            return
          }
        }

        // 載入任務列表
        const tasksResponse = await getDriverTasks(driverData.driverId)
        setTasks(tasksResponse.tasks)

        // 載入船邊櫃卡片（兩層聚合）
        const cardsResponse = await getShipsideCards()
        setShipsideCards(cardsResponse.cards)
        setShipsideTotalRemaining(cardsResponse.totalRemaining)
        setReservations(await getMyReservations())
      } catch (error) {
        console.error('載入資料失敗:', error)
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [lineUser, stateDriver, stateVehicle, stateVehicleState, navigate])

  // ========== 刷新任務列表 ==========
  const refreshTasks = useCallback(async () => {
    if (!driver) return
    setRefreshing(true)
    try {
      const [tasksResponse, cardsResponse, myReservations] = await Promise.all([
        getDriverTasks(driver.driverId),
        getShipsideCards(),
        getMyReservations(),
      ])
      setTasks(tasksResponse.tasks)
      setShipsideCards(cardsResponse.cards)
      setShipsideTotalRemaining(cardsResponse.totalRemaining)
      setReservations(myReservations)
    } catch (error) {
      console.error('刷新任務列表失敗:', error)
    } finally {
      setRefreshing(false)
      setNextRefreshIn(120) // 重置倒數
    }
  }, [driver])

  // ========== 每 2 分鐘自動刷新 ==========
  useEffect(() => {
    if (loading || !driver) return

    const timer = setInterval(() => {
      setNextRefreshIn(prev => {
        if (prev <= 1) {
          refreshTasks()
          return 120
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [loading, driver, refreshTasks])

  // ========== 切換回瀏覽器時刷新任務 ==========
  useEffect(() => {
    if (loading || !driver) return

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshTasks()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [loading, driver, refreshTasks])

  // ========== 完成任務模式處理 ==========
  useEffect(() => {
    // 只在完成任務模式、載入完成、上工中、且尚未處理時執行
    if (!isCompleteMode || loading || completeModeProcessed || notOnDutyDialogOpen) return

    // 找出執行中的任務
    const inProgressTasksList = tasks.filter(t => t.status === 'IN_PROGRESS')

    if (inProgressTasksList.length === 0) {
      // 沒有執行中的任務，不做任何處理
      setCompleteModeProcessed(true)
      return
    }

    if (inProgressTasksList.length === 1) {
      // 只有一筆執行中任務，直接開啟詳情
      setSelectedTask(inProgressTasksList[0])
      setContainerNumberInput(inProgressTasksList[0].containerNumber || '')
      setTaskDetailDialogOpen(true)
    } else {
      // 多筆執行中任務，展開列表
      setInProgressExpanded(true)
    }

    setCompleteModeProcessed(true)
  }, [isCompleteMode, loading, tasks, completeModeProcessed])

  // ========== Event Handlers ==========

  // Tab 切換處理
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue)
  }

  /**
   * 明細行識別鍵：卡片鍵 + 規格組合。
   * 同一卡片內可有多行（呎吋/空重/起訖不同），故不能只用 cardKey 或 containerId。
   */
  const rowKeyOf = (card: ShipsideCard, row: ShipsideCardRow) =>
    `${card.cardKey}|${row.containerSize}|${row.emptyLoaded}|${row.fromLocation}|${row.toLocation}`

  const toggleCardExpanded = (cardKey: string) => {
    setExpandedCards((prev) => {
      const next = new Set(prev)
      if (next.has(cardKey)) {
        next.delete(cardKey)
      } else {
        next.add(cardKey)
      }
      return next
    })
  }

  // ===== 名額卡：登記 / 選種類 / 取消 =====
  const reloadAll = async () => {
    const driverId = driver?.driverId || 'D001'
    const [tasksResponse, cardsResponse, myReservations] = await Promise.all([
      getDriverTasks(driverId),
      getShipsideCards(),
      getMyReservations(),
    ])
    setTasks(tasksResponse.tasks)
    setShipsideCards(cardsResponse.cards)
    setShipsideTotalRemaining(cardsResponse.totalRemaining)
    setReservations(myReservations)
  }

  const showMessage = (text: string, severity: 'success' | 'error' | 'warning', afterClose?: () => void) => {
    setMessageText(text)
    setMessageSeverity(severity)
    setCountdown(3)
    messageActionRef.current = afterClose ?? null
    setMessageDialogOpen(true)
  }

  /** 進口卡片一律「我要排隊」登記名額：排隊不等於接到，到場拿到櫃才選種類綁定（客戶 2026-09-10） */
  const handleReserveClick = (card: ShipsideCard) => {
    void doReserve(card)
  }

  const doReserve = async (card: ShipsideCard) => {
    setReservingKey(card.cardKey)
    try {
      const result = await reserveShipside(card)
      if (!result.success) {
        showMessage(result.message, 'error')
        return
      }
      await reloadAll()
      showMessage('已排隊。拿到櫃後，請到「我的任務」選擇載到的種類。', 'success', () => setCurrentTab(0))
    } catch (error) {
      console.error('登記名額失敗:', error)
      showMessage('排隊失敗，請稍後再試', 'error')
    } finally {
      setReservingKey(null)
    }
  }

  const handleBindReservation = async (reservation: GrabReservation, kind: '40' | '20' | '20x2') => {
    setBindingId(reservation.id)
    try {
      const result = await bindReservation(reservation.id, kind)
      await reloadAll()
      showMessage(result.message, result.success ? 'success' : 'warning')
    } catch (error) {
      console.error('選種類失敗:', error)
      showMessage('接單失敗，請稍後再試', 'error')
    } finally {
      setBindingId(null)
    }
  }

  const handleCancelReservation = async (reservation: GrabReservation) => {
    setBindingId(reservation.id)
    try {
      const result = await cancelReservation(reservation.id)
      await reloadAll()
      showMessage(result.message, result.success ? 'success' : 'error')
    } finally {
      setBindingId(null)
    }
  }

  /**
   * 搶單處理（卡片明細行）。
   *
   * @param wanted 要接的櫃數（1 或 2）。2 = 雙拖：連續呼叫兩次搶單 API。
   *
   * <p>雙拖不寫 mission.paired_mission_id（那是「新增任務勾雙拖」的業務事實，
   * 且連動 no_ship=2 影響薪資），這裡純粹是「同一趟拖兩櫃」的操作組合。
   * 我的任務端靠「兩筆執行中」推斷合卡，後端並行檢查已保證那必定是 20 呎同起訖。
   *
   * <p>第二櫃可能失敗（被別人搶走、或並行檢查不通過），此時**不回退第一櫃** ——
   * 第一櫃已進入執行中，退回等於幫司機做了他沒要求的決定。改以警告訊息如實告知。
   */
  const handleGrabShipsideRow = async (
    card: ShipsideCard,
    row: ShipsideCardRow,
    wanted: 1 | 2 = 1
  ) => {
    setGrabbingRowKey(rowKeyOf(card, row))
    setDoubleTowAsk(null)

    try {
      const driverId = driver?.driverId || 'D001'

      const first = await grabShipsideTask(card, row)

      // 第一櫃就失敗 → 完全沒接到，照原本的錯誤處理
      if (!first.success) {
        setMessageText(first.message)
        setMessageSeverity('error')
        setCountdown(3)
        setMessageDialogOpen(true)
        return
      }

      let grabbed = 1
      let secondFailReason = ''
      if (wanted === 2) {
        const second = await grabShipsideTask(card, row)
        if (second.success) {
          grabbed = 2
        } else {
          secondFailReason = second.message
        }
      }

      // 重新載入任務列表（確保資料一致性）
      const [tasksResponse, cardsResponse] = await Promise.all([
        getDriverTasks(driverId),
        getShipsideCards(),
      ])
      setTasks(tasksResponse.tasks)
      setShipsideCards(cardsResponse.cards)
      setShipsideTotalRemaining(cardsResponse.totalRemaining)

      if (wanted === 2 && grabbed === 1) {
        setMessageText(`只接到 1 櫃（第 2 櫃：${secondFailReason}）。已接的 1 櫃仍為執行中。`)
        setMessageSeverity('warning')
      } else if (grabbed === 2) {
        setMessageText('接單成功！已接 2 櫃（雙拖），任務已開始執行')
        setMessageSeverity('success')
      } else {
        setMessageText('接單成功！任務已開始執行')
        setMessageSeverity('success')
      }
      setCountdown(3)
      messageActionRef.current = () => setCurrentTab(0)
      setMessageDialogOpen(true)
    } catch (error) {
      console.error('搶單失敗:', error)
      setMessageText('搶單失敗，請稍後再試')
      setMessageSeverity('error')
      setCountdown(3)
      setMessageDialogOpen(true)
    } finally {
      setGrabbingRowKey(null)
    }
  }

  // ========== 取消執行中船邊櫃出口任務 ==========
  const handleOpenCancelDialog = (task: Task, partner?: Task | null) => {
    setCancelPartner(partner ?? null)
    setCancelTarget(task)
    setCancelDialogOpen(true)
  }

  const handleConfirmCancel = async (alsoCancelPaired: boolean) => {
    if (!cancelTarget) return
    setCancellingTask(true)
    try {
      const result = await cancelMyTask(
        cancelTarget.taskId,
        alsoCancelPaired ? (cancelPartner?.taskId ?? cancelTarget.pairedMissionId ?? null) : null,
      )
      if (result.success) {
        // 重抓任務列表 + 待搶單列表，確保資料一致性
        const driverId = driver?.driverId
        if (driverId) {
          const [tasksResp, cardsResp] = await Promise.all([
            getDriverTasks(driverId),
            getShipsideCards(),
          ])
          setTasks(tasksResp.tasks)
          setShipsideCards(cardsResp.cards)
          setShipsideTotalRemaining(cardsResp.totalRemaining)
        }
        setMessageText(result.message || '已取消，櫃已回搶單池')
        setMessageSeverity('success')
      } else {
        setMessageText(result.message || '取消失敗')
        setMessageSeverity('error')
      }
      setCountdown(3)
      setMessageDialogOpen(true)
    } catch (err) {
      console.error('取消任務失敗:', err)
      setMessageText('取消任務失敗')
      setMessageSeverity('error')
      setCountdown(3)
      setMessageDialogOpen(true)
    } finally {
      setCancellingTask(false)
      setCancelDialogOpen(false)
      setCancelTarget(null)
      setCancelPartner(null)
    }
  }

  const handleEndShift = async () => {
    // Rule-10: 呼叫後端 API 檢核是否有未完成任務阻擋交班
    setValidatingHandover(true)
    try {
      const validation = await validateHandover()

      if (!validation.canHandover) {
        // 有阻擋任務，顯示告警對話框
        setBlockingTasks(validation.blockingTasks)
        setBlockingSummary(validation.summary)
        setHandoverWarningDialogOpen(true)
      } else {
        // 沒有阻擋任務，直接導向交班頁面
        navigate('/handover', {
          state: {
            driver: driver,
            vehicle: vehicle,
            vehicleState: vehicleState,
          },
        })
      }
    } catch (error) {
      console.error('交班檢核失敗:', error)
      // 檢核失敗時，預設允許交班（避免阻擋使用者）
      navigate('/handover', {
        state: {
          driver: driver,
          vehicle: vehicle,
          vehicleState: vehicleState,
        },
      })
    } finally {
      setValidatingHandover(false)
    }
  }

  // 關閉交班阻擋對話框
  const handleCloseHandoverWarning = () => {
    setHandoverWarningDialogOpen(false)
    setBlockingTasks([])
    setBlockingSummary(null)
  }

  const handleOpenDailyReport = () => {
    // 導向歷史紀錄頁面
    navigate('/daily-report', {
      state: {
        driver: driver,
        vehicle: vehicle,
        vehicleState: vehicleState,
      },
    })
  }

  const handleOpenMissionAmendment = () => {
    navigate('/mission-amendment')
  }

  const handleOpenTaskDetail = (task: Task, dualMode = false) => {
    setSelectedTask(task)
    setIsDualMode(dualMode)
    setTaskDetailDialogOpen(true)
    // 如果任務需要司機手動輸入貨櫃號碼，初始化輸入框
    // 注意：needsManualContainerInput 但非 showScanContainerButton 的是「待管制室更新」，不進入編輯模式
    if (task.showScanContainerButton || task.isCntManualInput) {
      setContainerNumberInput(task.containerNumber || '')
      setIsEditingContainerNumber(!task.containerNumber) // 如果還沒有號碼，自動進入編輯模式
    } else {
      setContainerNumberInput('')
      setIsEditingContainerNumber(false)
    }
  }

  const handleCloseTaskDetail = () => {
    setTaskDetailDialogOpen(false)
    setSelectedTask(null)
    setContainerNumberInput('')
    setIsEditingContainerNumber(false)
  }

  const handleEditContainerNumber = () => {
    setIsEditingContainerNumber(true)
  }

  const handleSaveContainerNumber = async () => {
    if (!selectedTask) return

    // 貨櫃號碼 ISO 6346 Check Digit 驗證
    try {
      await validateContainerNo(containerNumberInput)
    } catch (validateError: any) {
      if (validateError.code === 'DSP-0706') {
        // 格式錯誤 → 硬擋
        setMessageText(validateError.statusDesc || '貨櫃號碼格式錯誤')
        setMessageSeverity('error')
        setMessageDialogOpen(true)
        return
      }
      if (validateError.code === 'DSP-0707') {
        // 檢核碼不符 → 用 MUI Dialog 警示
        const confirmed = await new Promise<boolean>((resolve) => {
          checkDigitResolveRef.current = resolve
          setCheckDigitWarningOpen(true)
        })
        if (!confirmed) return
      }
    }

    // 呼叫 API 更新貨櫃號碼
    const result = await updateContainerNumber(
      selectedTask.taskId,
      containerNumberInput,
      undefined, // sealNo
      'manual'   // inputMethod
    )

    if (result.success) {
      // 更新本地狀態
      const updatedContainerNo = result.data?.containerNo || containerNumberInput
      setTasks((prevTasks) =>
        prevTasks.map((task) =>
          task.taskId === selectedTask.taskId
            ? { ...task, containerNumber: updatedContainerNo, isCntManualInput: true }
            : task
        )
      )

      // 更新 selectedTask
      setSelectedTask({ ...selectedTask, containerNumber: updatedContainerNo, isCntManualInput: true })

      setIsEditingContainerNumber(false)

      // 顯示成功訊息
      setMessageText('貨櫃號碼已儲存')
      setMessageSeverity('success')
      setMessageDialogOpen(true)
    } else {
      // 顯示錯誤訊息
      setMessageText(result.message || '儲存失敗')
      setMessageSeverity('error')
      setMessageDialogOpen(true)
    }
  }

  const handleScanContainerNumber = () => {
    // 打開掃描提示對話框
    setScanDialogOpen(true)
  }

  // 開啟掃描器
  const handleStartScan = () => {
    setScanDialogOpen(false)
    setScannerDialogOpen(true)
  }

  // 掃描成功處理
  const handleScanSuccess = (decodedText: string) => {
    // 關閉掃描器
    setScannerDialogOpen(false)

    // 填入掃描結果（轉大寫，限制 11 碼）
    setContainerNumberInput(decodedText.toUpperCase().slice(0, 11))

    // 顯示成功訊息
    setMessageText(`已掃描貨櫃號碼：${decodedText}`)
    setMessageSeverity('success')
    setMessageDialogOpen(true)
  }

  // 關閉掃描器
  const handleCloseScannerDialog = () => {
    setScannerDialogOpen(false)
  }

  // 關閉掃描提示對話框
  const handleCloseScanDialog = () => {
    setScanDialogOpen(false)
  }


  const handleUpdateTaskStatus = async (newStatus: Task['status']) => {
    if (!selectedTask) return

    const prevStatus = selectedTask.status
    setUpdatingTaskStatus(true)

    try {
      // 雙拖：同步處理兩筆（spec §3.5）
      const pairedId = selectedTask.pairedMissionId ?? null
      const result = await updateTaskStatus(selectedTask.taskId, newStatus, pairedId)

      if (result.success) {
        // 更新本地任務列表（雙拖時兩筆都更新）
        setTasks((prevTasks) =>
          prevTasks.map((task) =>
            task.taskId === selectedTask.taskId
              || (pairedId !== null && task.taskId === pairedId)
              ? { ...task, status: newStatus }
              : task
          )
        )

        // 關閉對話框
        handleCloseTaskDetail()

        // 根據狀態變化顯示不同訊息
        let message = ''
        if (prevStatus === 'ASSIGNED' && newStatus === 'ACCEPTED') {
          message = '已接受指派'
        } else if (prevStatus === 'ACCEPTED' && newStatus === 'IN_PROGRESS') {
          message = '已開始執行'
        } else if (prevStatus === 'IN_PROGRESS' && newStatus === 'COMPLETED') {
          message = '任務已完成'
        }

        // 顯示成功訊息
        setMessageText(message)
        setMessageSeverity('success')
        setCountdown(3)
        setMessageDialogOpen(true)
      } else {
        setMessageText(result.message)
        setMessageSeverity('error')
        setCountdown(3)
        setMessageDialogOpen(true)
      }
    } catch (error) {
      console.error('更新任務狀態失敗:', error)
      setMessageText('更新任務狀態失敗，請稍後再試')
      setMessageSeverity('error')
      setCountdown(3)
      setMessageDialogOpen(true)
    } finally {
      setUpdatingTaskStatus(false)
    }
  }

  const handleRejectTask = async () => {
    if (!selectedTask) return

    try {
      // 雙拖：同步拒絕兩筆（spec §3.5.3）
      const pairedId = selectedTask.pairedMissionId ?? null
      const result = await rejectTask(selectedTask.taskId, undefined, pairedId)

      if (result.success) {
        // 從任務列表中移除（雙拖時兩筆都移除）
        setTasks((prevTasks) => prevTasks.filter((task) =>
          task.taskId !== selectedTask.taskId
          && (pairedId === null || task.taskId !== pairedId)
        ))

        // 關閉對話框
        handleCloseTaskDetail()

        // 顯示成功訊息
        setMessageText('已拒絕任務')
        setMessageSeverity('success')
        setCountdown(3)
        setMessageDialogOpen(true)
      } else {
        // 顯示錯誤訊息
        setMessageText(result.message || '拒絕任務失敗')
        setMessageSeverity('error')
        setCountdown(3)
        setMessageDialogOpen(true)
      }
    } catch (error) {
      console.error('拒絕任務失敗:', error)
      setMessageText('拒絕任務失敗')
      setMessageSeverity('error')
      setCountdown(3)
      setMessageDialogOpen(true)
    }
  }

  // 打開確認對話框
  const handleOpenConfirmDialog = (action: 'update' | 'reject', newStatus?: Task['status']) => {
    setConfirmAction(action)
    if (newStatus) {
      setPendingStatus(newStatus)
    }
    setConfirmDialogOpen(true)
  }

  // 確認執行操作
  const handleConfirmAction = async () => {
    setConfirmDialogOpen(false)

    if (confirmAction === 'reject') {
      handleRejectTask()
    } else if (confirmAction === 'update' && pendingStatus) {
      await handleUpdateTaskStatus(pendingStatus)
    }

    setPendingStatus(null)
  }

  // 取消操作
  const handleCancelAction = () => {
    setConfirmDialogOpen(false)
    setPendingStatus(null)
  }

  // 取得確認訊息
  const getConfirmMessage = () => {
    if (!selectedTask) return null

    // 任務辨識資訊區塊
    const taskInfo = (
      <Box sx={{ mt: 2, p: 1.5, backgroundColor: '#f5f5f5', borderRadius: 2, textAlign: 'left' }}>
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#6B705C', mb: 0.5 }}>
          {selectedTask.vesselCode} {selectedTask.vesselName && `- ${selectedTask.vesselName}`}
        </Typography>
        {!hideRoute(selectedTask) && (
          <Typography variant="body2" sx={{ color: '#333', mb: 0.5 }}>
            {selectedTask.originShortName || selectedTask.origin || '-'} → {selectedTask.destinationShortName || selectedTask.destination || '-'}
          </Typography>
        )}
        {selectedTask.containerNumber && (
          <Typography variant="body2" sx={{ color: '#666', fontFamily: 'monospace' }}>
            櫃號: {selectedTask.containerNumber}
          </Typography>
        )}
      </Box>
    )

    // 雙拖警告：若 paired，提示會同步處理另一筆（spec §3.5）
    const doubleTowWarning = selectedTask.pairedMissionId ? (
      <Typography
        variant="body2"
        sx={{ mt: 1.5, fontSize: '0.8rem', textAlign: 'left', color: '#B45309', fontWeight: 600 }}
      >
        ⚠️ 雙拖任務：將同步處理另一筆
        {selectedTask.pairedContainerNo && `（配對櫃號：${selectedTask.pairedContainerNo}）`}
      </Typography>
    ) : null

    if (confirmAction === 'reject') {
      return (
        <Box>
          <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
            確定要
            <Typography component="span" sx={{ fontWeight: 700, color: '#f44336', mx: 0.5 }}>
              拒絕
            </Typography>
            此任務嗎？
          </Typography>
          {taskInfo}
          {doubleTowWarning}
        </Box>
      )
    }

    if (!pendingStatus) return null

    const prevStatus = selectedTask.status

    // ASSIGNED → ACCEPTED: 接受指派
    if (prevStatus === 'ASSIGNED' && pendingStatus === 'ACCEPTED') {
      return (
        <Box>
          <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
            確定要
            <Typography component="span" sx={{ fontWeight: 700, color: '#9c27b0', mx: 0.5 }}>
              接受指派
            </Typography>
            此任務嗎？
          </Typography>
          {taskInfo}
          {doubleTowWarning}
        </Box>
      )
    }

    // ACCEPTED → IN_PROGRESS: 開始執行
    if (prevStatus === 'ACCEPTED' && pendingStatus === 'IN_PROGRESS') {
      return (
        <Box>
          <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
            確定要
            <Typography component="span" sx={{ fontWeight: 700, color: '#ff9800', mx: 0.5 }}>
              開始執行
            </Typography>
            此任務嗎？
          </Typography>
          {taskInfo}
          {doubleTowWarning}
        </Box>
      )
    }

    // IN_PROGRESS → COMPLETED: 完成任務
    if (prevStatus === 'IN_PROGRESS' && pendingStatus === 'COMPLETED') {
      return (
        <Box>
          <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
            確定要
            <Typography component="span" sx={{ fontWeight: 700, color: '#4caf50', mx: 0.5 }}>
              完成
            </Typography>
            此任務嗎？
          </Typography>
          {taskInfo}
          {doubleTowWarning}
        </Box>
      )
    }

    return null
  }

  const getTaskStatusLabel = (status: TaskStatus) => {
    switch (status) {
      case 'ASSIGNED':
        return '已指派'
      case 'ACCEPTED':
        return '已接受'
      case 'IN_PROGRESS':
        return '執行中'
      case 'COMPLETED':
        return '已完成'
      default:
        return status
    }
  }

  const getTaskStatusColor = (status: TaskStatus) => {
    switch (status) {
      case 'ASSIGNED':
        return '#2196f3' // 藍色
      case 'ACCEPTED':
        return '#9c27b0' // 紫色
      case 'IN_PROGRESS':
        return '#ff9800' // 橘色
      case 'COMPLETED':
        return '#4caf50' // 綠色
      default:
        return '#9e9e9e' // 灰色
    }
  }

  const formatTime = (dateString?: string) => {
    if (!dateString) return '--'
    const d = new Date(dateString)
    return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
  }

  // 排序任務:執行中 > 已接受 > 已指派 > 已完成
  const sortedTasks = [...tasks].sort((a, b) => {
    const statusOrder: Record<TaskStatus, number> = {
      IN_PROGRESS: 0,
      ACCEPTED: 1,
      ASSIGNED: 2,
      COMPLETED: 3,
      PENDING_RELEASE: 4,
      DISPATCHED: 5,
      WAITING_GRAB: 6,
      EXCEPTION: 7,
    }
    return (statusOrder[a.status] ?? 99) - (statusOrder[b.status] ?? 99)
  })

  // 雙拖配對偵測（用 paired_mission_id 互指；spec §3.5）
  // 同狀態的兩筆才算「可同步操作」的合卡
  const pairsByStatus = new Map<TaskStatus, Array<[Task, Task]>>()
  const pairedIds = new Set<string>()
  {
    const idMap = new Map<string, Task>()
    sortedTasks.forEach(t => idMap.set(t.taskId, t))
    sortedTasks.forEach(t => {
      if (pairedIds.has(t.taskId) || !t.pairedMissionId) return
      const partner = idMap.get(t.pairedMissionId)
      if (!partner || pairedIds.has(partner.taskId) || partner.status !== t.status) return
      const list = pairsByStatus.get(t.status) ?? []
      list.push([t, partner])
      pairsByStatus.set(t.status, list)
      pairedIds.add(t.taskId)
      pairedIds.add(partner.taskId)
    })
  }

  // （外車取消接單照抄此推斷：src/ext/ExtBatchPage.tsx 的 cancelPartnerOf，2026-09-29）
  // 推斷配對（0000291 後續 / 搶單雙拖）：搶單路徑不會寫 paired_mission_id，
  // 但司機實際上就是一趟拖兩櫃，顯示與操作都該合在一起。
  //
  // 為何只看「執行中」就安全：後端每一條進入執行中的路徑（搶單的
  // checkConcurrentLimit、指派後的 startTask）都擋並行上限，且唯一的例外是
  // 「兩筆都 20 呎且起訖相同」。所以司機同時有 2 筆執行中 ⟹ 必定同規格同起訖。
  // 這裡仍再比對一次規格屬防禦性，額外要求同船掛 —— 並行檢查不看船掛，
  // 跨船掛雙拖時合卡的共用表頭（船掛/船名）會顯示錯誤，寧可維持兩張卡。
  {
    const running = sortedTasks.filter(
      t => t.status === 'IN_PROGRESS' && !pairedIds.has(t.taskId)
    )
    if (running.length === 2) {
      const [a, b] = running
      const same =
        a.containerSize === b.containerSize &&
        String(a.containerSize) === '20' &&
        a.origin === b.origin &&
        a.destination === b.destination &&
        a.vesselCode === b.vesselCode
      if (same) {
        const list = pairsByStatus.get('IN_PROGRESS') ?? []
        list.push([a, b])
        pairsByStatus.set('IN_PROGRESS', list)
        pairedIds.add(a.taskId)
        pairedIds.add(b.taskId)
      }
    }
  }

  // 分離執行中任務與其他任務（雙拖個別卡會被過濾，由合卡負責 render）
  const singleTasks = sortedTasks.filter(t => !pairedIds.has(t.taskId))
  const inProgressTasks = singleTasks.filter(t => t.status === 'IN_PROGRESS')
  const otherTasks = singleTasks.filter(t => t.status !== 'IN_PROGRESS')
  const inProgressPairs = pairsByStatus.get('IN_PROGRESS') ?? []
  const assignedPairs = pairsByStatus.get('ASSIGNED') ?? []
  const acceptedPairs = pairsByStatus.get('ACCEPTED') ?? []

  // 批次完成雙拖（IN_PROGRESS → COMPLETED）
  const handleBatchComplete = async (pair: [Task, Task]) => {
    setUpdatingTaskStatus(true)
    try {
      const missionIds = [pair[0].taskId, pair[1].taskId]
      const result = await batchCompleteTask(missionIds)
      if (result.success) {
        setTasks(prev => prev.map(t =>
          missionIds.includes(t.taskId) ? { ...t, status: 'COMPLETED' as Task['status'] } : t
        ))
        setMessageText('任務已完成')
        setMessageSeverity('success')
        setCountdown(3)
        setMessageDialogOpen(true)
      } else {
        setMessageText(result.message)
        setMessageSeverity('error')
        setCountdown(3)
        setMessageDialogOpen(true)
      }
    } catch (error) {
      console.error('批次完成任務失敗:', error)
      setMessageText('批次完成任務失敗，請稍後再試')
      setMessageSeverity('error')
      setCountdown(3)
      setMessageDialogOpen(true)
    } finally {
      setUpdatingTaskStatus(false)
    }
  }

  // 渲染雙拖合卡（ASSIGNED / ACCEPTED / IN_PROGRESS 共用，spec §3.5 US-9）
  const renderDoubleTowCard = (pair: [Task, Task]) => {
    const [task1, task2] = pair
    const status = task1.status
    const needsContainer1 = task1.showScanContainerButton && !task1.containerNumber
    const needsContainer2 = task2.showScanContainerButton && !task2.containerNumber
    const cannotComplete = needsContainer1 || needsContainer2

    // 狀態 chip 配色
    const statusColor = getTaskStatusColor(status)
    const statusLabel = getTaskStatusLabel(status)
    const sizeChipLabel = `${task1.containerSize}呎×2`

    return (
      <Paper
        key={`pair-${task1.taskId}`}
        elevation={1}
        sx={{
          position: 'relative',
          borderRadius: 1.5,
          border: `2px solid ${statusColor}`,
          overflow: 'hidden',
          backgroundColor: `${statusColor}0a`,
        }}
      >
        <Box sx={{ p: 1.5 }}>
          {/* 頂部：按鈕 + 狀態 */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
            <Box sx={{ display: 'flex', gap: 0.75 }}>
              {/* ASSIGNED → 接受指派 + 拒絕（雙拖同步） */}
              {status === 'ASSIGNED' && (
                <>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={(e) => {
                      e.stopPropagation()
                      setSelectedTask(task1)
                      setTimeout(() => handleOpenConfirmDialog('update', 'ACCEPTED'), 0)
                    }}
                    disabled={updatingTaskStatus}
                    sx={{
                      fontSize: '0.75rem', py: 0.5, px: 1.5, minWidth: 'auto',
                      backgroundColor: '#9c27b0',
                      '&:hover': { backgroundColor: '#7b1fa2' },
                    }}
                  >
                    接受指派
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={(e) => {
                      e.stopPropagation()
                      setSelectedTask(task1)
                      setTimeout(() => handleOpenConfirmDialog('reject'), 0)
                    }}
                    disabled={updatingTaskStatus}
                    sx={{
                      fontSize: '0.75rem', py: 0.5, px: 1.5, minWidth: 'auto',
                      color: '#f44336', borderColor: '#f44336',
                      '&:hover': { borderColor: '#d32f2f', backgroundColor: 'rgba(244, 67, 54, 0.04)' },
                    }}
                  >
                    拒絕
                  </Button>
                </>
              )}

              {/* ACCEPTED → 開始執行（雙拖同步） */}
              {status === 'ACCEPTED' && (
                <Button
                  size="small"
                  variant="contained"
                  onClick={(e) => {
                    e.stopPropagation()
                    setSelectedTask(task1)
                    setTimeout(() => handleOpenConfirmDialog('update', 'IN_PROGRESS'), 0)
                  }}
                  disabled={updatingTaskStatus}
                  sx={{
                    fontSize: '0.75rem', py: 0.5, px: 1.5, minWidth: 'auto',
                    backgroundColor: '#ff9800',
                    '&:hover': { backgroundColor: '#f57c00' },
                  }}
                >
                  開始執行
                </Button>
              )}

              {/* IN_PROGRESS → 完成任務（保留批次完成） */}
              {status === 'IN_PROGRESS' && (
                <Button
                  size="small"
                  variant="contained"
                  onClick={(e) => { e.stopPropagation(); handleBatchComplete(pair) }}
                  disabled={updatingTaskStatus || cannotComplete}
                  sx={{
                    fontSize: '0.75rem', py: 0.5, px: 1.5, minWidth: 'auto',
                    backgroundColor: cannotComplete ? '#bdbdbd' : '#4caf50',
                    '&:hover': { backgroundColor: cannotComplete ? '#bdbdbd' : '#388e3c' },
                    '&.Mui-disabled': { backgroundColor: '#bdbdbd', color: '#ffffff' },
                  }}
                >
                  {cannotComplete ? '請先輸入櫃號' : '完成任務'}
                </Button>
              )}

              {/* 取消任務 — 僅 IN_PROGRESS + 出口。帶入配對夥伴，dialog 才能問「要不要兩筆都取消」；
                  搶單湊成的雙拖沒有 paired_mission_id，故須顯式傳入。 */}
              {status === 'IN_PROGRESS' && task1.importExportType === '出口' && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={(e) => { e.stopPropagation(); handleOpenCancelDialog(task1, task2) }}
                  disabled={cancellingTask}
                  sx={{
                    fontSize: '0.75rem', py: 0.5, px: 1.5, minWidth: 'auto',
                    borderColor: '#d32f2f', color: '#d32f2f',
                    '&:hover': { borderColor: '#b71c1c', backgroundColor: 'rgba(211, 47, 47, 0.04)' },
                  }}
                >
                  取消任務
                </Button>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              <Chip label={statusLabel} size="small" sx={{ backgroundColor: statusColor, color: 'white', fontWeight: 600, fontSize: '0.75rem' }} />
              <Chip label={sizeChipLabel} size="small" sx={{ backgroundColor: '#2196f3', color: 'white', fontWeight: 600, fontSize: '0.7rem' }} />
            </Box>
          </Box>

          {/* 共用資訊：船掛 + 起訖地 */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#6B705C', fontSize: '1.1rem' }}>
              {task1.vesselCode}
            </Typography>
            {task1.vesselName && (
              <Typography variant="body2" sx={{ fontWeight: 500, color: '#6B705C' }}>{task1.vesselName}</Typography>
            )}
            {task1.portCode && (
              <Chip label={task1.portCode} size="small" sx={{ fontWeight: 600, fontSize: '0.7rem', height: 22 }} />
            )}
          </Box>
          {!hideRoute(task1) && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{task1.originShortName || task1.origin || '-'}</Typography>
              <ArrowForwardIcon sx={{ fontSize: 16, mx: 0.5, color: '#6B705C' }} />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{task1.destinationShortName || task1.destination || '-'}</Typography>
            </Box>
          )}

          {/* 分別顯示兩個貨櫃資訊 */}
          {[task1, task2].map((task, idx) => (
            <Box key={task.taskId} sx={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              p: 0.75, mb: idx === 0 ? 0.5 : 0,
              backgroundColor: '#f5f5f5', borderRadius: 1,
              cursor: 'pointer',
              '&:hover': { backgroundColor: '#eeeeee' },
            }}
              onClick={() => handleOpenTaskDetail(task, true)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip label={`櫃${idx + 1}`} size="small" sx={{ fontSize: '0.7rem', height: 20 }} />
                <Typography variant="body2" sx={{ fontWeight: 600, color: task.containerNumber ? '#333' : '#bbb' }}>
                  {task.containerNumber || '未填寫'}
                </Typography>
              </Box>
              {task.showScanContainerButton && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleOpenTaskDetail(task, true)
                  }}
                  sx={{ fontSize: '0.7rem', py: 0.25, px: 1, minWidth: 'auto' }}
                >
                  {task.containerNumber ? '修改櫃號' : '輸入櫃號'}
                </Button>
              )}
            </Box>
          ))}
        </Box>
      </Paper>
    )
  }

  // 渲染任務卡片
  const renderTaskCard = (task: Task) => {
    const isInProgress = task.status === 'IN_PROGRESS'
    return (
      <Paper
        key={task.taskId}
        elevation={1}
        sx={{
          position: 'relative',
          borderRadius: 1.5,
          border: isInProgress ? '2px solid #ff9800' : '1px solid #e0e0e0',
          overflow: 'hidden',
          transition: 'all 0.2s',
          backgroundColor: isInProgress ? 'rgba(255, 152, 0, 0.02)' : 'white',
          '&:hover': {
            elevation: 2,
            boxShadow: isInProgress
              ? '0 2px 8px rgba(255, 152, 0, 0.3)'
              : '0 2px 6px rgba(0,0,0,0.1)',
          },
        }}
      >
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => handleOpenTaskDetail(task)}
            sx={{
              p: 1.5,
            }}
          >
            <Box sx={{ width: '100%' }}>
              {/* 頂部：操作按鈕 + 狀態標籤 */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                {/* 左側：操作按鈕 */}
                <Box sx={{ display: 'flex', gap: 0.75 }}>
                  {/* 已指派 (ASSIGNED) → 接受指派 / 拒絕任務 */}
                  {task.status === 'ASSIGNED' && (
                    <>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={(e) => {
                          e.stopPropagation()
                          setSelectedTask(task)
                          setTimeout(() => handleOpenConfirmDialog('update', 'ACCEPTED'), 0)
                        }}
                        disabled={updatingTaskStatus}
                        sx={{
                          fontSize: '0.75rem',
                          py: 0.5,
                          px: 1.5,
                          minWidth: 'auto',
                          backgroundColor: '#9c27b0',
                          '&:hover': { backgroundColor: '#7b1fa2' },
                        }}
                      >
                        接受指派
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={(e) => {
                          e.stopPropagation()
                          setSelectedTask(task)
                          setTimeout(() => handleOpenConfirmDialog('reject'), 0)
                        }}
                        disabled={updatingTaskStatus}
                        sx={{
                          fontSize: '0.75rem',
                          py: 0.5,
                          px: 1.5,
                          minWidth: 'auto',
                          color: '#f44336',
                          borderColor: '#f44336',
                          '&:hover': { borderColor: '#d32f2f', backgroundColor: 'rgba(244, 67, 54, 0.04)' },
                        }}
                      >
                        拒絕
                      </Button>
                    </>
                  )}

                  {/* 已接單 (ACCEPTED) → 開始執行 */}
                  {task.status === 'ACCEPTED' && (
                    <Button
                      size="small"
                      variant="contained"
                      onClick={(e) => {
                        e.stopPropagation()
                        setSelectedTask(task)
                        setTimeout(() => handleOpenConfirmDialog('update', 'IN_PROGRESS'), 0)
                      }}
                      disabled={updatingTaskStatus}
                      sx={{
                        fontSize: '0.75rem',
                        py: 0.5,
                        px: 1.5,
                        minWidth: 'auto',
                        backgroundColor: '#ff9800',
                        '&:hover': { backgroundColor: '#f57c00' },
                      }}
                    >
                      開始執行
                    </Button>
                  )}

                  {/* 執行中 (IN_PROGRESS) → 完成任務 */}
                  {task.status === 'IN_PROGRESS' && (() => {
                    // 檢查是否需要手動輸入貨櫃號碼但尚未填寫
                    const needsContainerNumber = task.showScanContainerButton === true
                    const hasContainerNumber = !!task.containerNumber
                    const cannotComplete = needsContainerNumber && !hasContainerNumber

                    return (
                      <Button
                        size="small"
                        variant="contained"
                        onClick={(e) => {
                          e.stopPropagation()
                          setSelectedTask(task)
                          setTimeout(() => handleOpenConfirmDialog('update', 'COMPLETED'), 0)
                        }}
                        disabled={updatingTaskStatus || cannotComplete}
                        sx={{
                          fontSize: '0.75rem',
                          py: 0.5,
                          px: 1.5,
                          minWidth: 'auto',
                          backgroundColor: cannotComplete ? '#bdbdbd' : '#4caf50',
                          '&:hover': { backgroundColor: cannotComplete ? '#bdbdbd' : '#388e3c' },
                          '&.Mui-disabled': {
                            backgroundColor: '#bdbdbd',
                            color: '#ffffff',
                          },
                        }}
                      >
                        {cannotComplete ? '請先輸入櫃號' : '完成任務'}
                      </Button>
                    )
                  })()}

                  {/* 取消任務 — 僅 IN_PROGRESS + 出口船邊櫃 (io_type='E') */}
                  {task.status === 'IN_PROGRESS' && task.importExportType === '出口' && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleOpenCancelDialog(task)
                      }}
                      disabled={cancellingTask}
                      sx={{
                        fontSize: '0.75rem',
                        py: 0.5,
                        px: 1.5,
                        minWidth: 'auto',
                        borderColor: '#d32f2f',
                        color: '#d32f2f',
                        '&:hover': {
                          borderColor: '#b71c1c',
                          backgroundColor: 'rgba(211, 47, 47, 0.04)',
                        },
                      }}
                    >
                      取消任務
                    </Button>
                  )}
                </Box>

                {/* 右側：狀態標籤 */}
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                  <Chip
                    label={getTaskStatusLabel(task.status)}
                    size="small"
                    sx={{
                      backgroundColor: getTaskStatusColor(task.status),
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                    }}
                  />
                </Box>
              </Box>

              {/* 第一行：船掛 + 船名 + 碼頭（v2 #8：W+5 位為補登虛擬船掛，加 chip） */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, gap: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#6B705C', fontSize: '1.1rem' }}>
                  {task.vesselCode}
                </Typography>
                {task.vesselCode && /^W\d{5}$/.test(task.vesselCode) && (
                  <Chip label="補登" size="small" sx={{
                    fontWeight: 600, fontSize: '0.65rem', height: 20,
                    backgroundColor: '#FFF4E5', color: '#B45309',
                  }} />
                )}
                {task.vesselName && (
                  <Typography variant="body2" sx={{ fontWeight: 500, color: '#6B705C' }}>
                    {task.vesselName}
                  </Typography>
                )}
                {task.portCode && (
                  <Chip label={task.portCode} size="small" sx={{ fontWeight: 600, fontSize: '0.7rem', height: 22 }} />
                )}
              </Box>

              {/* 第二行：起點 → 迄點 + 卸貨港（進口 + 搶單來的不顯示起訖） */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                {!hideRoute(task) && (
                  <>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {task.originShortName || task.origin || '-'}
                    </Typography>
                    <ArrowForwardIcon sx={{ fontSize: 16, mx: 0.5, color: '#6B705C' }} />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {task.destinationShortName || task.destination || '-'}
                    </Typography>
                  </>
                )}
                {task.importExportType === '出口' && task.dischargePort && (
                  <Typography variant="body2" sx={{ fontWeight: 500, color: '#6B705C', ml: 0.5 }}>
                    ({task.dischargePort})
                  </Typography>
                )}
              </Box>

              {/* 放櫃備註（紅字 + 省略 + Info icon） */}
              {task.releaseRemark && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, gap: 0.5 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#d32f2f',
                      fontWeight: 600,
                      flex: 1,
                      minWidth: 0,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    備註：{task.releaseRemark}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation()
                      setRemarkDetailContent(task.releaseRemark || '')
                      setRemarkDetailOpen(true)
                    }}
                    sx={{ color: '#d32f2f', p: 0.25 }}
                  >
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}

              {/* 第三行：貨櫃號碼 + 呎吋 + 空/重櫃 */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                  {/* 貨櫃號碼 */}
                  {task.showScanContainerButton && !task.containerNumber && !task.isCntManualInput ? (
                    // 顯示輸入按鈕（貨櫃號碼為空且起訖地不屬於聯興且未手動輸入過）
                    //
                    // 2026-08：改為「手輸為主、相機為備案」（客戶回覆司機偏好手輸）。
                    // 原本此處是實心的「掃描貨櫃號碼」按鈕，且點下後 setTimeout 300ms
                    // 自動彈出掃描提示框 —— 司機沒有選擇機會，要手輸得先按「離開」才看得到
                    // 輸入框。現在直接開詳情，handleOpenTaskDetail 內的
                    // setIsEditingContainerNumber(!task.containerNumber) 會讓輸入框直接出現。
                    // 相機仍在詳情視窗內以次要按鈕提供。
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={(e) => {
                        e.stopPropagation() // 防止觸發任務詳情對話框
                        handleOpenTaskDetail(task)
                      }}
                      sx={{
                        backgroundColor: '#2196f3',
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        px: 1.5,
                        py: 0.5,
                        boxShadow: '0 2px 4px rgba(33, 150, 243, 0.3)',
                        '&:hover': {
                          backgroundColor: '#1976d2',
                          boxShadow: '0 3px 6px rgba(33, 150, 243, 0.4)',
                        },
                      }}
                    >
                      輸入櫃號
                    </Button>
                  ) : task.isCntManualInput && task.containerNumber ? (
                    // 已透過 LINE@ 手動輸入的貨櫃號碼 - 綠色標記（可編輯）
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ color: '#4caf50', fontWeight: 600 }}>
                        {task.containerNumber}
                      </Typography>
                      <CheckCircleIcon sx={{ fontSize: '1rem', color: '#4caf50', ml: 0.3 }} />
                    </Box>
                  ) : task.needsManualContainerInput && !task.containerNumber ? (
                    // 會經聯興管制室自動取得貨櫃號碼
                    <Chip
                      label="待管制室更新"
                      size="small"
                      sx={{
                        backgroundColor: '#e3f2fd',
                        color: '#1565c0',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        height: 24,
                      }}
                    />
                  ) : task.needsManualContainerInput && task.containerNumber ? (
                    // 已手動輸入的貨櫃號碼 - 特別標記
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ color: '#9c27b0', fontWeight: 600 }}>
                        {task.containerNumber}
                      </Typography>
                      <TouchAppIcon sx={{ fontSize: '1rem', color: '#9c27b0', ml: 0.3 }} />
                    </Box>
                  ) : (
                    // 一般貨櫃號碼
                    task.containerNumber && (
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {task.containerNumber}
                      </Typography>
                    )
                  )}

                  {/* 呎吋 */}
                  <Typography variant="body2" color="text.secondary">
                    {task.containerSize ? `${task.containerSize} 呎` : ''}
                  </Typography>

                  {/* 空/重櫃 */}
                  <Typography variant="body2" color="text.secondary">
                    {task.containerType}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    詳情
                  </Typography>
                  <ChevronRightIcon sx={{ color: '#9e9e9e', fontSize: 20 }} />
                </Box>
              </Box>
            </Box>
          </ListItemButton>
        </ListItem>
      </Paper>
    )
  }

  // ========== 載入中 ==========
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #FFE8D6 0%, #DDBEA9 100%)',
        }}
      >
        <CircularProgress size={60} sx={{ color: '#6B705C' }} />
        <Typography variant="body1" sx={{ mt: 2, color: '#6B705C' }}>
          載入中...
        </Typography>
      </Box>
    )
  }

  // ========== Render ==========
  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #FFE8D6 0%, #DDBEA9 100%)',
        py: 1.5,
      }}
    >
      <Container maxWidth="md">
        {/* 頁面標題 - 包含駕駛資訊 */}
        <Paper
          elevation={2}
          sx={{
            p: 1.5,
            mb: 1.5,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #6B705C 0%, #A5A58D 100%)',
            color: 'white',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flex: 1 }}>
              <AssignmentIcon sx={{ fontSize: { xs: 32, sm: 40 }, flexShrink: 0 }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                  任務列表
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, fontSize: { xs: '0.75rem', sm: '0.875rem' }, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {driver?.driverName || '-'} ({driver?.driverId || '-'})
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.75, flexShrink: 0 }}>
              <Button
                variant="contained"
                onClick={() => !refreshing && refreshTasks()}
                disabled={refreshing}
                startIcon={<RefreshIcon sx={{ fontSize: { xs: 16, sm: 20 }, animation: refreshing ? 'spin 1s linear infinite' : 'none', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} />}
                sx={{
                  backgroundColor: '#6B705C',
                  color: 'white',
                  fontWeight: 600,
                  borderRadius: 2,
                  px: { xs: 1, sm: 1.5 },
                  py: { xs: 0.5, sm: 1 },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  minWidth: 'auto',
                  '&:hover': {
                    backgroundColor: '#5a5f4e',
                  },
                  '& .MuiButton-startIcon': {
                    marginRight: { xs: '2px', sm: '8px' },
                  },
                }}
              >
                {refreshing ? '更新中' : '刷新'}
              </Button>
              <Button
                variant="contained"
                startIcon={validatingHandover ? <CircularProgress size={16} color="inherit" /> : <ExitToAppIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />}
                onClick={handleEndShift}
                disabled={validatingHandover}
                sx={{
                  backgroundColor: '#ff9800',
                  color: 'white',
                  fontWeight: 600,
                  borderRadius: 2,
                  px: { xs: 1, sm: 1.5 },
                  py: { xs: 0.5, sm: 1 },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  minWidth: 'auto',
                  boxShadow: '0 2px 8px rgba(255, 152, 0, 0.3)',
                  '&:hover': {
                    backgroundColor: '#f57c00',
                    boxShadow: '0 4px 12px rgba(255, 152, 0, 0.4)',
                  },
                  '& .MuiButton-startIcon': {
                    marginRight: { xs: '2px', sm: '8px' },
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#ffcc80',
                    color: 'white',
                  },
                }}
              >
                {validatingHandover ? '檢核中...' : '交班'}
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* 任務列表區域 */}
        <Paper elevation={3} sx={{ p: 3, mb: 2, borderRadius: 3, position: 'relative' }}>
          {/* 自動更新倒數 - 右上角，點擊可手動刷新 */}
          <Typography
            variant="caption"
            onClick={() => !refreshing && refreshTasks()}
            sx={{
              position: 'absolute',
              top: 8,
              right: 12,
              color: '#bbb',
              fontSize: '0.6rem',
              cursor: 'pointer',
              userSelect: 'none',
              '&:hover': { color: '#888' },
            }}
          >
            {refreshing ? '更新中...' : nextRefreshIn >= 60 ? `${Math.floor(nextRefreshIn / 60)}:${String(nextRefreshIn % 60).padStart(2, '0')} 後更新列表` : `${nextRefreshIn}秒後更新列表`}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#6B705C' }}>
              待執行任務
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<EditNoteIcon />}
                onClick={handleOpenMissionAmendment}
                sx={{
                  color: '#6B705C',
                  borderColor: '#6B705C',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: '#5A5F4A',
                    backgroundColor: 'rgba(107, 112, 92, 0.08)',
                  },
                }}
              >
                任務補登
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DescriptionIcon />}
                onClick={handleOpenDailyReport}
                sx={{
                  color: '#6B705C',
                  borderColor: '#6B705C',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: '#5A5F4A',
                    backgroundColor: 'rgba(107, 112, 92, 0.08)',
                  },
                }}
              >
                歷史紀錄
              </Button>
            </Box>
          </Box>

          {/* Tab 頁籤 */}
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            sx={{
              mb: 1,
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': {
                fontWeight: 600,
                fontSize: '1rem',
                color: '#757575',
                '&.Mui-selected': {
                  color: '#6B705C',
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#6B705C',
                height: 3,
              },
            }}
          >
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>我的任務</span>
                  <Chip
                    label={tasks.filter(t => t.status === 'ASSIGNED' || t.status === 'ACCEPTED').length}
                    size="small"
                    sx={{
                      backgroundColor: '#2196f3',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      height: 20,
                      minWidth: 20,
                    }}
                  />
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>船邊櫃任務</span>
                  <Chip
                    label={shipsideTotalRemaining}
                    size="small"
                    sx={{
                      backgroundColor: '#f44336',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      height: 20,
                      minWidth: 20,
                    }}
                  />
                </Box>
              }
            />
          </Tabs>

          {/* Tab 內容 - 我的任務 */}
          {currentTab === 0 && (
            <>
              {/* 名額卡：已登記排隊、尚未選種類。不等於一定有櫃，實際以現場為準。 */}
              {reservations.map((r) => {
                const busy = bindingId === r.id
                return (
                  <Paper
                    key={r.id}
                    elevation={2}
                    sx={{ mb: 2, borderRadius: 2, border: '2px solid #1976d2', boxShadow: 'inset 0 0 0 1px #1976d255, inset 0 0 12px #1976d233', overflow: 'hidden' }}
                  >
                    <Box sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: '#6B705C', fontSize: '1.1rem' }}>
                            {r.vesselCode}
                          </Typography>
                          {r.vesselName && (
                            <Typography variant="body2" sx={{ fontWeight: 500, color: '#6B705C' }}>{r.vesselName}</Typography>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Chip label="進口" size="small" sx={{ backgroundColor: '#1976d2', color: 'white', fontWeight: 600, fontSize: '0.7rem', height: 22 }} />
                          <Chip label="排隊中" size="small" sx={{ backgroundColor: '#ff9800', color: 'white', fontWeight: 600, fontSize: '0.7rem', height: 22 }} />
                        </Box>
                      </Box>
                      <Typography variant="body2" sx={{ color: '#6B705C', mb: 1 }}>
                        剩餘 {r.remainingContainers} 櫃 · 排隊 {r.queueCount} 人
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#9e9e9e', display: 'block', mb: 1 }}>
                        可選種類會依現場與其他批次調整
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                        拿到櫃後請選擇：
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {r.kinds.map((k) => (
                          <Button
                            key={k}
                            variant="contained"
                            disabled={busy || bindingId !== null}
                            onClick={() => setBindConfirm({ reservation: r, kind: k })}
                            sx={{ flex: 1, minWidth: 96, backgroundColor: '#4caf50', color: 'white', fontWeight: 700, '&:hover': { backgroundColor: '#388e3c' }, '&:disabled': { backgroundColor: '#a5d6a7' } }}
                          >
                            {busy ? <CircularProgress size={18} sx={{ color: 'white' }} /> : KIND_LABEL[k] ?? k}
                          </Button>
                        ))}
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                        <Button size="small" color="inherit" disabled={busy} onClick={() => handleCancelReservation(r)} sx={{ color: '#9e9e9e' }}>
                          取消排隊
                        </Button>
                      </Box>
                    </Box>
                  </Paper>
                )
              })}
              {sortedTasks.length === 0 && reservations.length === 0 ? (
                <Box
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    backgroundColor: '#f5f5f5',
                    borderRadius: 2,
                  }}
                >
                  <AssignmentIcon sx={{ fontSize: 48, color: '#9e9e9e', mb: 2 }} />
                  <Typography variant="body1" color="text.secondary">
                    目前沒有任務
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    暫無分配給您的任務
                  </Typography>
                </Box>
              ) : (
            <List sx={{ p: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {/* 執行中雙拖合卡（先顯示，spec §3.5 US-9） */}
              {inProgressPairs.map(pair => renderDoubleTowCard(pair))}

              {/* 執行中任務區塊 - 堆疊顯示（單筆，不含雙拖） */}
              {inProgressTasks.length > 0 && (
                <Box sx={{ position: 'relative', mb: inProgressTasks.length > 1 && !inProgressExpanded ? 2 : 0 }}>
                  {/* 堆疊背景效果 - 只在收縮且有多筆時顯示 */}
                  {!inProgressExpanded && inProgressTasks.length > 1 && (
                    <>
                      {/* 第二層陰影 */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          left: 4,
                          right: 4,
                          height: 'calc(100% - 8px)',
                          backgroundColor: 'rgba(255, 152, 0, 0.08)',
                          borderRadius: 1.5,
                          border: '1px solid rgba(255, 152, 0, 0.3)',
                          zIndex: 0,
                        }}
                      />
                      {/* 第三層陰影 (如果有3筆以上) */}
                      {inProgressTasks.length > 2 && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 16,
                            left: 8,
                            right: 8,
                            height: 'calc(100% - 16px)',
                            backgroundColor: 'rgba(255, 152, 0, 0.04)',
                            borderRadius: 1.5,
                            border: '1px solid rgba(255, 152, 0, 0.2)',
                            zIndex: -1,
                          }}
                        />
                      )}
                    </>
                  )}

                  {/* 顯示執行中單筆任務 */}
                  <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {(inProgressExpanded ? inProgressTasks : [inProgressTasks[0]]).map(renderTaskCard)}
                  </Box>

                  {/* 展開/收起按鈕 */}
                  {inProgressTasks.length > 1 && (
                    <Button
                      fullWidth
                      onClick={() => setInProgressExpanded(!inProgressExpanded)}
                      startIcon={inProgressExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      sx={{
                        mt: 1,
                        color: '#ff9800',
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        backgroundColor: 'rgba(255, 152, 0, 0.08)',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 152, 0, 0.15)',
                        },
                      }}
                    >
                      {inProgressExpanded ? '收起執行中任務' : `展開執行中任務 (${inProgressTasks.length} 筆)`}
                    </Button>
                  )}
                </Box>
              )}

              {/* 其他狀態雙拖合卡（ASSIGNED / ACCEPTED） */}
              {assignedPairs.map(pair => renderDoubleTowCard(pair))}
              {acceptedPairs.map(pair => renderDoubleTowCard(pair))}

              {/* 其他任務（單筆，不含雙拖） */}
              {otherTasks.map(renderTaskCard)}
            </List>
              )}
            </>
          )}

          {/* Tab 內容 - 船邊櫃任務 */}
          {currentTab === 1 && (
            <>
              {shipsideCards.length === 0 ? (
                <Box
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    backgroundColor: '#f5f5f5',
                    borderRadius: 2,
                  }}
                >
                  <AssignmentIcon sx={{ fontSize: 48, color: '#9e9e9e', mb: 2 }} />
                  <Typography variant="body1" color="text.secondary">
                    目前沒有船邊櫃任務
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    暫無需要搶單的船邊櫃任務
                  </Typography>
                </Box>
              ) : (
                <List sx={{ p: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {shipsideCards.map((card) => {
                    // 明細行依可搶數量降冪；超過 3 行收合，避免大批次把畫面撐爆
                    // 進口（名額卡）：起訖對司機無意義，各尺寸只留一筆總計（40 呎 / 20 呎），空重櫃一致才顯示
                    const sortedRows: ShipsideCardRow[] = isImportCard(card)
                      ? Object.values(
                          card.rows.reduce<Record<string, ShipsideCardRow>>((acc, r) => {
                            const k = r.containerSize || '-'
                            const cur = acc[k]
                            acc[k] = cur
                              ? {
                                  ...cur,
                                  totalCount: cur.totalCount + r.totalCount,
                                  remainingCount: cur.remainingCount + r.remainingCount,
                                  containerType: cur.containerType === r.containerType ? cur.containerType : ('' as ShipsideCardRow['containerType']),
                                }
                              : { ...r, fromLocation: '', fromShortName: '', toLocation: '', toShortName: '' }
                            return acc
                          }, {}),
                        ).sort((a, b) => String(b.containerSize).localeCompare(String(a.containerSize)))
                      : [...card.rows].sort((a, b) => b.remainingCount - a.remainingCount)
                    const isExpanded = expandedCards.has(card.cardKey)
                    const visibleRows = isExpanded ? sortedRows : sortedRows.slice(0, 3)
                    const hiddenCount = sortedRows.length - visibleRows.length

                    // 進出口色讓整張卡「穿上」顏色：外框實線 + 貼著外框往內暈開的淡色（外深內淺），
                    // 內容區維持白底不影響閱讀。右上角 Chip 太小，客戶反映一眼分不出進口／出口。
                    const ioColor = card.importExportType === '進口' ? '#1976d2'
                      : card.importExportType === '出口' ? '#ed6c02' : null
                    return (
                      <Paper
                        key={card.cardKey}
                        elevation={2}
                        sx={{
                          position: 'relative',
                          borderRadius: 2,
                          border: ioColor ? `2px solid ${ioColor}` : '1px solid #e0e0e0',
                          boxShadow: ioColor
                            ? `inset 0 0 0 1px ${ioColor}55, inset 0 0 12px ${ioColor}33`
                            : undefined,
                          overflow: 'hidden',
                          backgroundColor: 'white',
                        }}
                      >
                        <Box sx={{ p: 2 }}>
                          {/* ===== 外層：批次共通資訊（船掛、船名、碼頭、進出別） ===== */}
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 700, color: '#6B705C', fontSize: '1.1rem' }}>
                                  {card.vesselCode}
                                </Typography>
                                {card.vesselName && (
                                  <Typography variant="body2" sx={{ fontWeight: 500, color: '#6B705C' }}>
                                    {card.vesselName}
                                  </Typography>
                                )}
                              </Box>
                              {card.portCode && (
                                <Typography variant="body2" color="text.secondary">
                                  {card.portCode}
                                </Typography>
                              )}
                            </Box>
                            {card.importExportType && (
                              <Chip
                                label={card.importExportType}
                                size="small"
                                sx={{
                                  backgroundColor: card.importExportType === '進口' ? '#1976d2' : card.importExportType === '出口' ? '#ed6c02' : '#9e9e9e',
                                  color: 'white',
                                  fontWeight: 600,
                                  fontSize: '0.7rem',
                                  height: 22,
                                }}
                              />
                            )}
                          </Box>

                          {/* 放櫃備註（紅字 + 省略 + Info icon） */}
                          {card.releaseRemark && (
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 0.5 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: '#d32f2f',
                                  fontWeight: 600,
                                  flex: 1,
                                  minWidth: 0,
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                備註：{card.releaseRemark}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setRemarkDetailContent(card.releaseRemark || '')
                                  setRemarkDetailOpen(true)
                                }}
                                sx={{ color: '#d32f2f', p: 0.25 }}
                              >
                                <InfoOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          )}

                          {/* 卡片可搶總數 + 明細行數（讓司機一眼看出這是一批多櫃）
                              進口（名額卡）：忠實呈現三個數字——剩餘櫃數（真相）、預估名額、排隊人數 */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#ff9800' }}>
                              {isImportCard(card) ? `剩餘 ${card.totalRemaining} 櫃` : `可搶 ${card.totalRemaining} 櫃`}
                            </Typography>
                            {isImportCard(card) ? (
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#6B705C' }}>
                                · 排隊 {card.queueCount ?? 0} 人
                              </Typography>
                            ) : sortedRows.length > 1 && (
                              <Typography variant="caption" color="text.secondary">
                                （{sortedRows.length} 種規格）
                              </Typography>
                            )}
                          </Box>

                          {/* ===== 內層：規格組合明細行 =====
                              批次內呎吋/空重/起訖不保證一致，故差異欄位放這裡，各自帶可搶數與接單鈕。
                              同一行內必定同規格同起訖 —— 這是「接第二櫃必然通過 20 呎雙拖檢查」的前提。 */}
                          {/* 進口卡片不列尺寸明細：碼頭發櫃不照批次那幾顆給，列 20/40 各幾顆沒有意義（v2） */}
                          {!isImportCard(card) && (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {visibleRows.map((row) => {
                              const key = isImportCard(card) ? `${card.cardKey}#${row.containerSize}` : rowKeyOf(card, row)
                              const grabbing = grabbingRowKey === key
                              const importCard = isImportCard(card)
                              return (
                                <Box
                                  key={key}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 1,
                                    p: 1.25,
                                    backgroundColor: '#f5f5f5',
                                    borderRadius: 1.5,
                                  }}
                                >
                                  <Box sx={{ minWidth: 0, flex: 1 }}>
                                    {/* 起點 → 迄點（進口卡片不顯示：司機無從選起訖） */}
                                    {!importCard && (
                                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                          {row.fromShortName || row.fromLocation || '-'}
                                        </Typography>
                                        <ArrowForwardIcon sx={{ fontSize: 16, mx: 0.5, color: '#6B705C' }} />
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                          {row.toShortName || row.toLocation || '-'}
                                        </Typography>
                                      </Box>
                                    )}
                                    {/* 呎吋 + 空重 + 剩餘/總數 */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                      <Chip
                                        label={row.containerSize ? `${row.containerSize} 呎` : '-'}
                                        size="small"
                                        sx={{ fontWeight: 600, fontSize: '0.7rem', height: 20, backgroundColor: '#2196f3', color: 'white' }}
                                      />
                                      <Typography variant="body2" sx={{ color: '#6B705C' }}>
                                        {row.containerType}
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#ff9800' }}>
                                        剩餘 {row.remainingCount} / {row.totalCount}
                                      </Typography>
                                    </Box>
                                  </Box>
                                  {/* 一次一櫃：不提供數量輸入（搶單後自動推進至執行中，並行上限 2 筆）；進口卡片改為整卡一顆名額鍵 */}
                                  {!importCard && (
                                  <Button
                                    variant="contained"
                                    size="medium"
                                    disabled={grabbingRowKey !== null || holdsReservation}
                                    onClick={() => {
                                      // 20 呎且同一明細行還有 2 櫃以上 → 先問要不要雙拖。
                                      // 同一明細行必定同規格同起訖，正好是後端並行檢查放行的條件。
                                      if (String(row.containerSize) === '20' && row.remainingCount >= 2) {
                                        setDoubleTowAsk({ card, row })
                                      } else {
                                        handleGrabShipsideRow(card, row, 1)
                                      }
                                    }}
                                    sx={{
                                      flexShrink: 0,
                                      backgroundColor: '#4caf50',
                                      color: 'white',
                                      fontWeight: 600,
                                      fontSize: '0.9rem',
                                      px: 2.5,
                                      py: 1,
                                      boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)',
                                      '&:hover': {
                                        backgroundColor: '#388e3c',
                                        boxShadow: '0 4px 12px rgba(76, 175, 80, 0.4)',
                                      },
                                      '&:disabled': {
                                        backgroundColor: '#a5d6a7',
                                      },
                                    }}
                                  >
                                    {grabbing ? (
                                      <CircularProgress size={20} sx={{ color: 'white' }} />
                                    ) : (
                                      '我要接單'
                                    )}
                                  </Button>
                                  )}
                                </Box>
                              )
                            })}
                          </Box>
                          )}

                          {/* 進口卡片一律「我要排隊」：只登記名額，不綁 mission；到場選種類才綁（排隊不等於接到） */}
                          {isImportCard(card) && (
                            <Button
                              fullWidth
                              variant="contained"
                              size="large"
                              disabled={reservingKey !== null || card.totalRemaining <= 0 || grabBlockedLabel !== null}
                              onClick={() => handleReserveClick(card)}
                              sx={{
                                mt: 1.5,
                                backgroundColor: '#4caf50',
                                color: 'white',
                                fontWeight: 700,
                                fontSize: '1rem',
                                py: 1.25,
                                boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)',
                                '&:hover': { backgroundColor: '#388e3c' },
                                '&:disabled': { backgroundColor: '#a5d6a7' },
                              }}
                            >
                              {reservingKey === card.cardKey
                                ? <CircularProgress size={22} sx={{ color: 'white' }} />
                                : (grabBlockedLabel ?? '我要排隊')}
                            </Button>
                          )}

                          {/* 展開/收起（明細行超過 3 行才出現） */}
                          {sortedRows.length > 3 && (
                            <Button
                              fullWidth
                              onClick={() => toggleCardExpanded(card.cardKey)}
                              startIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                              sx={{
                                mt: 1,
                                color: '#6B705C',
                                fontSize: '0.85rem',
                                fontWeight: 600,
                                backgroundColor: 'rgba(107, 112, 92, 0.08)',
                                '&:hover': { backgroundColor: 'rgba(107, 112, 92, 0.15)' },
                              }}
                            >
                              {isExpanded ? '收起規格' : `展開其餘 ${hiddenCount} 種規格`}
                            </Button>
                          )}
                        </Box>
                      </Paper>
                    )
                  })}
                </List>
              )}
            </>
          )}
        </Paper>

        {/* 任務詳情對話框 */}
        <Dialog
          open={taskDetailDialogOpen}
          onClose={handleCloseTaskDetail}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, borderBottom: '1px solid #e0e0e0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography component="span" sx={{ fontSize: '1.2rem', fontWeight: 600 }}>任務詳情</Typography>
              {selectedTask && (
                <Chip
                  label={getTaskStatusLabel(selectedTask.status)}
                  size="small"
                  sx={{
                    backgroundColor: getTaskStatusColor(selectedTask.status),
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                  }}
                />
              )}
            </Box>
            <Box
              onClick={handleCloseTaskDetail}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: 'pointer',
                '&:hover': {
                  opacity: 0.7,
                },
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: (theme) => theme.palette.grey[600],
                  fontSize: '0.75rem',
                }}
              >
                關閉視窗
              </Typography>
              <IconButton
                aria-label="close"
                sx={{
                  padding: 0.5,
                  color: (theme) => theme.palette.grey[500],
                  pointerEvents: 'none',
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            {selectedTask && (
              <Box sx={{ px: 3 }}>

                {/* 需求時間（整合時間、起迄點、貨櫃資訊） */}
                <Paper elevation={0} sx={{ p: 1.5, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#6B705C' }}>
                    需求時間
                  </Typography>
                  <Grid container spacing={0.5}>
                    {/* 時間：MM/DD HH:MM ~ MM/DD HH:MM */}
                    <Grid item xs={12}>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#333' }}>
                        {formatTime(selectedTask.estimatedStartTime) || '-'} ~ {formatTime(selectedTask.estimatedEndTime) || '-'}
                      </Typography>
                    </Grid>
                    {/* 起訖點：[起點] → [訖點]（進口 + 搶單來的不顯示） */}
                    {!hideRoute(selectedTask) && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary" component="span" sx={{ fontSize: '0.85rem' }}>起訖點: </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#333' }} component="span">
                          {selectedTask.originShortName || selectedTask.origin || '-'} → {selectedTask.destinationShortName || selectedTask.destination || '-'}
                        </Typography>
                      </Grid>
                    )}
                    {/* 卸貨港（僅出口顯示）- 暫時隱藏 */}
                    {/* {selectedTask.importExportType === '出口' && selectedTask.dischargePort && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary" component="span" sx={{ fontSize: '0.85rem' }}>卸貨港: </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#333' }} component="span">
                          {selectedTask.dischargePort}
                        </Typography>
                      </Grid>
                    )} */}
                    {/* 貨櫃摘要：[空重櫃] [櫃長] */}
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Chip label={selectedTask.containerType || '-'} size="small" variant="outlined" sx={{ fontWeight: 600, fontSize: '0.85rem' }} />
                        <Chip label={selectedTask.containerSize ? `${selectedTask.containerSize} 呎` : '-'} size="small" variant="outlined" sx={{ fontWeight: 600, fontSize: '0.85rem' }} />
                      </Box>
                    </Grid>
                    {/* 貨櫃號碼 */}
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                        {isEditingContainerNumber ? (
                          !containerNumberInput ? (
                            <Chip label="待輸入櫃號" size="small" sx={{ fontWeight: 600, fontSize: '0.85rem', backgroundColor: '#ed6c02', color: 'white' }} />
                          ) : null
                        ) : (containerNumberInput || selectedTask.containerNumber) ? (
                          <>
                            <Chip label={containerNumberInput || selectedTask.containerNumber} size="small" variant="outlined" sx={{ fontWeight: 600, fontSize: '0.85rem' }} />
                            {(selectedTask.showScanContainerButton || selectedTask.isCntManualInput) && (
                              <Chip
                                label="修改"
                                size="small"
                                onClick={handleEditContainerNumber}
                                sx={{ fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer', color: '#6B705C', borderColor: '#6B705C' }}
                                variant="outlined"
                                icon={<EditIcon sx={{ fontSize: '0.85rem !important' }} />}
                              />
                            )}
                          </>
                        ) : selectedTask.needsManualContainerInput && !selectedTask.showScanContainerButton ? (
                          <Chip label="待管制室更新" size="small" sx={{ fontWeight: 600, fontSize: '0.85rem', backgroundColor: '#1565c0', color: 'white' }} />
                        ) : null}
                      </Box>
                    </Grid>
                    {/* 貨櫃號碼 - 編輯模式（掃描/手動輸入） */}
                    {(selectedTask.showScanContainerButton || selectedTask.isCntManualInput) && isEditingContainerNumber && (
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <TextField
                            fullWidth
                            size="small"
                            value={containerNumberInput}
                            onChange={(e) => setContainerNumberInput(e.target.value.toUpperCase().slice(0, 11))}
                            placeholder="請輸入貨櫃號碼"
                            inputProps={{ maxLength: 11 }}
                            sx={{ '& .MuiInputBase-root': { height: 32, fontSize: '0.85rem' } }}
                          />
                          {/* 2026-08：手輸為主 —— 儲存佔滿主要位置，掃描降為右側次要小鈕。
                              原本兩顆都是 fullWidth 各佔一半、掃描還在左邊（先讀到的位置）。 */}
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Button
                              fullWidth
                              variant="contained"
                              onClick={handleSaveContainerNumber}
                              disabled={!containerNumberInput.trim()}
                              startIcon={<SaveIcon sx={{ fontSize: '1rem' }} />}
                              sx={{
                                py: 0.5,
                                backgroundColor: '#6B705C',
                                fontWeight: 600,
                                fontSize: '0.85rem',
                                minHeight: 0,
                                '&:hover': {
                                  backgroundColor: '#5A5F4A',
                                },
                              }}
                            >
                              儲存
                            </Button>
                            <Button
                              variant="text"
                              onClick={handleScanContainerNumber}
                              startIcon={<QrCodeScannerIcon sx={{ fontSize: '1rem' }} />}
                              sx={{
                                py: 0.5,
                                px: 1.5,
                                flexShrink: 0,
                                color: '#888',
                                fontWeight: 500,
                                fontSize: '0.85rem',
                                minHeight: 0,
                                whiteSpace: 'nowrap',
                                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                              }}
                            >
                              掃描
                            </Button>
                          </Box>
                        </Box>
                      </Grid>
                    )}

                  </Grid>
                </Paper>

                <Paper elevation={0} sx={{ p: 1.5, mt: 1.5, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', mb: 0.5 }}>貨主資訊</Typography>
                      <Box sx={{ backgroundColor: '#fff', p: 1, borderRadius: 1, border: '1px solid #e0e0e0', minHeight: 40 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem', color: '#333', whiteSpace: 'pre-wrap' }}>
                          {selectedTask.consignee || '-'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary" component="span" sx={{ fontSize: '0.85rem' }}>備註: </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#333' }} component="span">{selectedTask.notes || '-'}</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Box>
            )}
          </DialogContent>
          <DialogActions
            sx={{
              p: 1.5,
              flexDirection: 'column',
              gap: 1,
              alignItems: 'center',
              width: '100%',
              '& > :not(style) ~ :not(style)': {
                marginLeft: 0,
              },
            }}
          >
            {/* 狀態切換按鈕 */}
            {/* 已指派 (ASSIGNED) → 可接受指派或拒絕 */}
            {selectedTask && selectedTask.status === 'ASSIGNED' && (
              <>
                <Button
                  fullWidth
                  onClick={() => handleOpenConfirmDialog('update', 'ACCEPTED')}
                  variant="contained"
                  size="medium"
                  disabled={updatingTaskStatus}
                  sx={{
                    fontSize: '1rem',
                    py: 1,
                    backgroundColor: '#9c27b0',
                    border: '1px solid #9c27b0',
                    boxSizing: 'border-box',
                    '&:hover': {
                      backgroundColor: '#7b1fa2',
                      borderColor: '#7b1fa2',
                    },
                  }}
                >
                  {updatingTaskStatus ? (
                    <CircularProgress size={20} sx={{ color: 'white' }} />
                  ) : (
                    '接受指派'
                  )}
                </Button>
                <Button
                  fullWidth
                  onClick={() => handleOpenConfirmDialog('reject')}
                  variant="outlined"
                  size="medium"
                  disabled={updatingTaskStatus}
                  sx={{
                    fontSize: '1rem',
                    py: 1,
                    color: '#f44336',
                    borderColor: '#f44336',
                    boxSizing: 'border-box',
                    '&:hover': {
                      borderColor: '#d32f2f',
                      backgroundColor: 'rgba(244, 67, 54, 0.04)',
                    },
                  }}
                >
                  拒絕任務
                </Button>
              </>
            )}

            {/* 已接單 (ACCEPTED) → 可開始執行（雙櫃模式下不顯示） */}
            {selectedTask && selectedTask.status === 'ACCEPTED' && !isDualMode && (
              <Button
                fullWidth
                onClick={() => handleOpenConfirmDialog('update', 'IN_PROGRESS')}
                variant="contained"
                size="medium"
                disabled={updatingTaskStatus}
                sx={{
                  fontSize: '1rem',
                  py: 1,
                  backgroundColor: '#ff9800',
                  border: '1px solid #ff9800',
                  boxSizing: 'border-box',
                  '&:hover': {
                    backgroundColor: '#f57c00',
                    borderColor: '#f57c00',
                  },
                }}
              >
                {updatingTaskStatus ? (
                  <CircularProgress size={20} sx={{ color: 'white' }} />
                ) : (
                  '開始執行'
                )}
              </Button>
            )}

            {/* 執行中 (IN_PROGRESS) → 可完成任務 */}
            {selectedTask && selectedTask.status === 'IN_PROGRESS' && (() => {
              // 雙櫃模式：隱藏完成按鈕，顯示提示
              if (isDualMode) {
                return (
                  <Box sx={{ width: '100%' }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#ff9800',
                        textAlign: 'center',
                        py: 1,
                        fontSize: '0.85rem',
                        fontWeight: 500,
                      }}
                    >
                      此為雙櫃任務，請返回任務列表統一完成
                    </Typography>
                  </Box>
                )
              }

              // 檢查是否需要手動輸入貨櫃號碼但尚未填寫
              const needsContainerNumber = selectedTask.showScanContainerButton === true
              const hasContainerNumber = !!selectedTask.containerNumber
              const cannotComplete = needsContainerNumber && !hasContainerNumber

              return (
                <Box sx={{ width: '100%' }}>
                  {cannotComplete && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#f44336',
                        textAlign: 'center',
                        mb: 1,
                        fontSize: '0.875rem',
                      }}
                    >
                      請先掃描或輸入貨櫃號碼
                    </Typography>
                  )}
                  <Button
                    fullWidth
                    onClick={() => handleOpenConfirmDialog('update', 'COMPLETED')}
                    variant="contained"
                    size="medium"
                    disabled={updatingTaskStatus || cannotComplete}
                    sx={{
                      fontSize: '1rem',
                      py: 1,
                      backgroundColor: cannotComplete ? '#bdbdbd' : '#4caf50',
                      border: `1px solid ${cannotComplete ? '#bdbdbd' : '#4caf50'}`,
                      boxSizing: 'border-box',
                      '&:hover': {
                        backgroundColor: cannotComplete ? '#bdbdbd' : '#388e3c',
                        borderColor: cannotComplete ? '#bdbdbd' : '#388e3c',
                      },
                      '&.Mui-disabled': {
                        backgroundColor: '#bdbdbd',
                        color: '#ffffff',
                      },
                    }}
                  >
                    {updatingTaskStatus ? (
                      <CircularProgress size={20} sx={{ color: 'white' }} />
                    ) : (
                      '完成任務'
                    )}
                  </Button>
                </Box>
              )
            })()}
          </DialogActions>
        </Dialog>

        {/* 確認對話框 */}
        <Dialog
          open={confirmDialogOpen}
          onClose={handleCancelAction}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
            },
          }}
        >
          <DialogTitle sx={{ fontSize: '1.2rem', fontWeight: 600, textAlign: 'center', pt: 3 }}>
            確認操作
          </DialogTitle>
          <DialogContent sx={{ textAlign: 'center', py: 3 }}>
            {getConfirmMessage()}
          </DialogContent>
          <DialogActions sx={{ p: 2.5, justifyContent: 'center', gap: 2 }}>
            <Button
              onClick={handleCancelAction}
              variant="outlined"
              size="large"
              sx={{ fontSize: '1rem', px: 4, minWidth: 100 }}
            >
              取消
            </Button>
            <Button
              onClick={handleConfirmAction}
              variant="contained"
              size="large"
              sx={{ fontSize: '1rem', px: 4, minWidth: 100 }}
            >
              確定
            </Button>
          </DialogActions>
        </Dialog>

        {/* 貨櫃號碼檢核碼不符警示對話框 */}
        <Dialog
          open={checkDigitWarningOpen}
          onClose={() => {
            setCheckDigitWarningOpen(false)
            checkDigitResolveRef.current?.(false)
          }}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ fontSize: '1.2rem', fontWeight: 600, textAlign: 'center', pt: 3 }}>
            貨櫃號碼檢核碼不符
          </DialogTitle>
          <DialogContent sx={{ textAlign: 'center', py: 3 }}>
            是否仍要儲存？
          </DialogContent>
          <DialogActions sx={{ p: 2.5, justifyContent: 'center', gap: 2 }}>
            <Button
              onClick={() => {
                setCheckDigitWarningOpen(false)
                checkDigitResolveRef.current?.(false)
              }}
              variant="outlined"
              size="large"
              sx={{ fontSize: '1rem', px: 4, minWidth: 100 }}
            >
              取消
            </Button>
            <Button
              onClick={() => {
                setCheckDigitWarningOpen(false)
                checkDigitResolveRef.current?.(true)
              }}
              variant="contained"
              size="large"
              sx={{ fontSize: '1rem', px: 4, minWidth: 100 }}
            >
              確認儲存
            </Button>
          </DialogActions>
        </Dialog>

        {/* 掃描提示對話框 */}
        <Dialog
          open={scanDialogOpen}
          onClose={handleCloseScanDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
            },
          }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
            <QrCodeScannerIcon sx={{ color: '#2196f3', fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              條碼掃描
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ py: 2 }}>
              <Typography variant="body1" sx={{ mb: 2, fontWeight: 600, color: '#ff9800' }}>
                ⚠️ 使用提示
              </Typography>

              <Box sx={{ mb: 2, pl: 2 }}>
                <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'flex-start', fontWeight: 600, color: '#2196f3' }}>
                  <span style={{ marginRight: '8px' }}>⚡</span>
                  <span>極速掃描技巧：</span>
                </Typography>
                <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'flex-start' }}>
                  <span style={{ marginRight: '8px' }}>•</span>
                  <span>60 FPS 極速掃描，每秒 60 次辨識嘗試</span>
                </Typography>
                <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'flex-start' }}>
                  <span style={{ marginRight: '8px' }}>•</span>
                  <span>保持距離 10-20 公分，讓條碼清晰顯示</span>
                </Typography>
                <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'flex-start' }}>
                  <span style={{ marginRight: '8px' }}>•</span>
                  <span>光線充足，避免反光或陰影</span>
                </Typography>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <span style={{ marginRight: '8px' }}>•</span>
                  <span>對準掃描框，系統會瞬間辨識</span>
                </Typography>
              </Box>

              <Box
                sx={{
                  p: 2,
                  backgroundColor: '#fff3e0',
                  borderRadius: 2,
                  border: '2px solid #ff9800',
                }}
              >
                <Typography variant="caption" sx={{ color: '#e65100', fontWeight: 700, display: 'block', mb: 0.5 }}>
                  ⚡ 極速掃描模式已啟用
                </Typography>
                <Typography variant="caption" sx={{ color: '#f57c00', display: 'block' }}>
                  • 支援格式：CODE-128、CODE-39
                </Typography>
                <Typography variant="caption" sx={{ color: '#f57c00', display: 'block' }}>
                  • 60 FPS 極速掃描（每秒 60 次）
                </Typography>
                <Typography variant="caption" sx={{ color: '#f57c00', display: 'block' }}>
                  • 300×100 長條形掃描框
                </Typography>
                <Typography variant="caption" sx={{ color: '#ff6f00', display: 'block', mt: 0.5, fontWeight: 700 }}>
                  ⚡ 對準條碼，瞬間完成！
                </Typography>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2.5, gap: 1 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleCloseScanDialog}
              sx={{
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                borderColor: '#9e9e9e',
                color: '#616161',
                '&:hover': {
                  borderColor: '#757575',
                  backgroundColor: 'rgba(158, 158, 158, 0.04)',
                },
              }}
            >
              離開
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={handleStartScan}
              startIcon={<QrCodeScannerIcon />}
              sx={{
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                backgroundColor: '#2196f3',
                '&:hover': {
                  backgroundColor: '#1976d2',
                },
              }}
            >
              開始掃描
            </Button>
          </DialogActions>
        </Dialog>

        {/* 掃描器元件 */}
        <BarcodeScanner
          open={scannerDialogOpen}
          onClose={handleCloseScannerDialog}
          onSuccess={handleScanSuccess}
          title="掃描貨櫃號碼"
        />

        {/* 訊息對話框 */}
        <Dialog
          open={messageDialogOpen}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              p: 1,
            },
          }}
        >
          <DialogContent sx={{ textAlign: 'center', py: 4 }}>
            {messageSeverity === 'success' ? (
              <CheckCircleIcon sx={{ fontSize: 60, color: '#4caf50', mb: 2 }} />
            ) : messageSeverity === 'warning' ? (
              /* 部分成功（如雙拖只接到 1 櫃）—— 用紅色會讓司機以為整批都沒接到 */
              <ErrorOutlineIcon sx={{ fontSize: 60, color: '#ff9800', mb: 2 }} />
            ) : (
              <ErrorOutlineIcon sx={{ fontSize: 60, color: '#f44336', mb: 2 }} />
            )}
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
              {messageText}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {countdown} 秒後自動關閉
            </Typography>
          </DialogContent>
          {/* 0000273：加 OK 按鈕，點了立即關閉並提前完成倒數後要做的事（如切頁籤） */}
          <DialogActions sx={{ justifyContent: 'center', pb: 3, pt: 0 }}>
            <Button
              onClick={closeMessageDialog}
              variant="contained"
              sx={{
                minWidth: 140,
                fontWeight: 700,
                bgcolor: messageSeverity === 'success' ? '#4caf50'
                  : messageSeverity === 'warning' ? '#ff9800' : '#f44336',
                '&:hover': {
                  bgcolor: messageSeverity === 'success' ? '#43a047'
                    : messageSeverity === 'warning' ? '#f57c00' : '#e53935',
                },
              }}
            >
              確認
            </Button>
          </DialogActions>
        </Dialog>

        {/* 未上工提示對話框 */}
        <Dialog
          open={notOnDutyDialogOpen}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              textAlign: 'center',
            },
          }}
        >
          <DialogContent sx={{ py: 4 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <ErrorOutlineIcon sx={{ fontSize: 80, color: '#ff9800' }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#6B705C' }}>
                您尚未上工
              </Typography>
              <Typography variant="body1" color="text.secondary">
                請先接班後再查看任務列表
              </Typography>
              <Typography variant="body2" color="text.secondary">
                系統將在 {notOnDutyCountdown} 秒後自動返回首頁
              </Typography>
            </Box>
          </DialogContent>
          {/* OK：立即返回首頁（提前完成倒數後動作） */}
          <DialogActions sx={{ justifyContent: 'center', pb: 3, pt: 0 }}>
            <Button
              onClick={() => navigate('/home', { replace: true })}
              variant="contained"
              sx={{ minWidth: 140, fontWeight: 700, bgcolor: '#ff9800', '&:hover': { bgcolor: '#fb8c00' } }}
            >
              確認
            </Button>
          </DialogActions>
        </Dialog>

        {/* 交班阻擋對話框 (Rule-10) */}
        <Dialog
          open={handoverWarningDialogOpen}
          onClose={handleCloseHandoverWarning}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
            },
          }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#d32f2f' }}>
            <ErrorOutlineIcon />
            無法交班
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 2 }}>
              您有 <strong>{blockingSummary?.totalBlockingCount || blockingTasks.length}</strong> 筆任務需要處理：
            </Typography>

            {/* 分類摘要 */}
            {blockingSummary && (
              <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {blockingSummary.inProgressCount > 0 && (
                  <Chip
                    label={`執行中：${blockingSummary.inProgressCount} 筆`}
                    size="small"
                    sx={{ bgcolor: '#ffebee', color: '#c62828' }}
                  />
                )}
                {blockingSummary.acceptedTodayCount > 0 && (
                  <Chip
                    label={`已接受今日單：${blockingSummary.acceptedTodayCount} 筆`}
                    size="small"
                    sx={{ bgcolor: '#fff3e0', color: '#e65100' }}
                  />
                )}
                {blockingSummary.assignedTodayCount > 0 && (
                  <Chip
                    label={`已指派今日單：${blockingSummary.assignedTodayCount} 筆`}
                    size="small"
                    sx={{ bgcolor: '#e3f2fd', color: '#1565c0' }}
                  />
                )}
              </Box>
            )}

            {/* 任務清單 */}
            <List sx={{ bgcolor: '#ffebee', borderRadius: 2, p: 1, maxHeight: 300, overflow: 'auto' }}>
              {blockingTasks.map((task) => (
                <ListItem key={task.taskId} sx={{ py: 0.5, display: 'block' }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    • [{task.statusName}] {task.vesselName || task.vesselCode}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ pl: 1.5 }}>
                    {task.originShortName || task.origin || '-'} → {task.destinationShortName || task.destination || '-'}
                    {task.blockReasonMessage && ` (${task.blockReasonMessage})`}
                  </Typography>
                </ListItem>
              ))}
            </List>

            <Typography variant="body2" color="error" sx={{ mt: 2, fontWeight: 500 }}>
              請先處理上述任務後再進行交班
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2.5 }}>
            <Button
              onClick={handleCloseHandoverWarning}
              variant="contained"
              color="primary"
              fullWidth
            >
              返回處理
            </Button>
          </DialogActions>
        </Dialog>

        {/* 取消任務 Dialog — 偵測到雙拖配對時三選項 */}
        <Dialog
          open={cancelDialogOpen}
          onClose={() => !cancellingTask && setCancelDialogOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 700, color: '#d32f2f' }}>取消任務</DialogTitle>
          <DialogContent>
            {cancelTarget && (
              <>
                <Box sx={{ mb: 2, p: 1.5, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#6B705C', mb: 0.5 }}>
                    {cancelTarget.vesselCode} {cancelTarget.vesselName && `- ${cancelTarget.vesselName}`}
                  </Typography>
                  {!hideRoute(cancelTarget) && (
                    <Typography variant="body2" sx={{ color: '#333' }}>
                      {cancelTarget.originShortName || cancelTarget.origin || '-'}
                      {' → '}
                      {cancelTarget.destinationShortName || cancelTarget.destination || '-'}
                    </Typography>
                  )}
                  {cancelTarget.containerNumber && (
                    <Typography variant="body2" sx={{ color: '#666', fontFamily: 'monospace', mt: 0.5 }}>
                      櫃號: {cancelTarget.containerNumber}
                    </Typography>
                  )}
                </Box>
                {(cancelPartner?.taskId ?? cancelTarget.pairedMissionId) ? (
                  <Typography variant="body2" color="text.secondary">
                    偵測到雙拖配對（另一櫃同 20 呎、同起訖地，由您執行中）。
                    取消後櫃會回搶單池，其他司機可重新搶單。
                    <br />
                    <Box component="span" sx={{ fontWeight: 600, color: '#d32f2f' }}>
                      是否一併取消第二櫃？
                    </Box>
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    取消後該櫃會回到搶單池，其他司機可搶。
                  </Typography>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1, justifyContent: 'flex-end' }}>
            <Button
              onClick={() => { setCancelDialogOpen(false); setCancelPartner(null) }}
              disabled={cancellingTask}
              sx={{ color: '#666' }}
            >
              不取消
            </Button>
            {(cancelPartner?.taskId ?? cancelTarget?.pairedMissionId) ? (
              <>
                <Button
                  onClick={() => handleConfirmCancel(false)}
                  disabled={cancellingTask}
                  variant="outlined"
                  sx={{ borderColor: '#d32f2f', color: '#d32f2f' }}
                >
                  只取消這筆
                </Button>
                <Button
                  onClick={() => handleConfirmCancel(true)}
                  disabled={cancellingTask}
                  variant="contained"
                  sx={{ backgroundColor: '#d32f2f', '&:hover': { backgroundColor: '#b71c1c' } }}
                >
                  兩筆都取消
                </Button>
              </>
            ) : (
              <Button
                onClick={() => handleConfirmCancel(false)}
                disabled={cancellingTask}
                variant="contained"
                sx={{ backgroundColor: '#d32f2f', '&:hover': { backgroundColor: '#b71c1c' } }}
              >
                確定取消
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* 名額卡選種類 double check：綁定後不能改 */}
        <Dialog
          open={bindConfirm !== null}
          onClose={() => setBindConfirm(null)}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ fontWeight: 700, color: '#6B705C', textAlign: 'center', pt: 3 }}>
            確認載到的是「{bindConfirm ? KIND_LABEL[bindConfirm.kind] ?? bindConfirm.kind : ''}」？
          </DialogTitle>
          <DialogContent>
            {bindConfirm && (
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  船掛 <strong>{bindConfirm.reservation.vesselCode}</strong>
                  {bindConfirm.kind === '20x2' ? '，將一次綁定 2 櫃' : ''}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  確認後立即綁定任務並開始執行，之後不能更改種類。
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ flexDirection: 'column', gap: 1, px: 3, pb: 3 }}>
            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                if (!bindConfirm) return
                const { reservation, kind } = bindConfirm
                setBindConfirm(null)
                void handleBindReservation(reservation, kind)
              }}
              sx={{ backgroundColor: '#4caf50', color: 'white', fontWeight: 700, '&:hover': { backgroundColor: '#388e3c' } }}
            >
              確認，就是{bindConfirm ? KIND_LABEL[bindConfirm.kind] ?? bindConfirm.kind : ''}
            </Button>
            <Button fullWidth variant="outlined" onClick={() => setBindConfirm(null)} sx={{ color: '#6B705C', borderColor: '#6B705C' }}>
              再看看
            </Button>
          </DialogActions>
        </Dialog>

        {/* 雙拖詢問 Dialog（20 呎且同一明細行剩餘 >= 2） */}
        <Dialog
          open={doubleTowAsk !== null}
          onClose={() => setDoubleTowAsk(null)}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ fontWeight: 700, color: '#6B705C', textAlign: 'center', pt: 3 }}>
            要接雙拖嗎？
          </DialogTitle>
          <DialogContent>
            {doubleTowAsk && (
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{ p: 1.5, backgroundColor: '#f5f5f5', borderRadius: 2, mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#6B705C', mb: 0.5 }}>
                    {doubleTowAsk.card.vesselCode}
                    {doubleTowAsk.card.vesselName && ` - ${doubleTowAsk.card.vesselName}`}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#333' }}>
                    {doubleTowAsk.row.fromShortName || doubleTowAsk.row.fromLocation || '-'}
                    {' → '}
                    {doubleTowAsk.row.toShortName || doubleTowAsk.row.toLocation || '-'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#ff9800', fontWeight: 700, mt: 0.5 }}>
                    20 呎 {doubleTowAsk.row.containerType}，剩餘 {doubleTowAsk.row.remainingCount} 櫃
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  這批同起訖的 20 呎櫃還有 {doubleTowAsk.row.remainingCount} 櫃，可以一趟拖兩櫃。
                </Typography>
                {/* 如實告知可能只接到一櫃 —— 兩次搶單之間別人可能先搶走 */}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  若第 2 櫃剛好被別人搶走，會只接到 1 櫃（已接的那櫃仍有效）。
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2.5, flexDirection: 'column', gap: 1 }}>
            <Button
              fullWidth
              variant="contained"
              onClick={() => doubleTowAsk && handleGrabShipsideRow(doubleTowAsk.card, doubleTowAsk.row, 2)}
              sx={{
                py: 1.2, fontSize: '1rem', fontWeight: 700,
                backgroundColor: '#4caf50',
                '&:hover': { backgroundColor: '#388e3c' },
              }}
            >
              接 2 櫃（雙拖）
            </Button>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => doubleTowAsk && handleGrabShipsideRow(doubleTowAsk.card, doubleTowAsk.row, 1)}
              sx={{
                py: 1.2, fontSize: '1rem', fontWeight: 600,
                color: '#6B705C', borderColor: '#6B705C',
              }}
            >
              只接 1 櫃
            </Button>
            <Button
              fullWidth
              onClick={() => setDoubleTowAsk(null)}
              sx={{ color: '#888' }}
            >
              取消
            </Button>
          </DialogActions>
        </Dialog>

        {/* 放櫃備註詳情 Dialog */}
        <Dialog
          open={remarkDetailOpen}
          onClose={() => setRemarkDetailOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ color: '#d32f2f', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoOutlinedIcon /> 放櫃備註
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ color: '#d32f2f', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {remarkDetailContent || '（無備註）'}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRemarkDetailOpen(false)} variant="contained">
              關閉
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  )
}

export default TaskBrowsePage
